import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { useAuth } from '../Context/AuthContext';
import { useCompany } from '../Context/CompanyProvider';
import { getFavorites, addFavorite, removeFavorite } from '../services/favoritesService';
import { trackLotView } from '../services/lotHistoryService';
import { useMapInstance } from '../Map/hooks/useMapInstance';
import MapUnavailable, { MapErrorBoundary } from '../Map/components/MapUnavailable';
import { useMapOrientation } from '../Map/hooks/useMapOrientation';
import { useMapViewInfo } from '../Map/hooks/useMapViewInfo';
import { usePmtilesLayers } from '../Map/hooks/usePmtilesLayers';
import { useLotSelection, pickBestLotFeatureAtPoint, getFeatureLotId, getLotRepresentativeLatLng } from '../Map/hooks/useLotSelection';
import { useLotViewQuota } from '../Map/hooks/useLotViewQuota';
import {
  buildLotViewQuotaExceededMessage,
  isLotViewQuotaBlockedPayload,
} from '../Map/utils/lotViewQuota';
import { useParcelFilters } from '../Map/hooks/useParcelFilters';
import { useOfficialUnitMapFilter } from '../Map/hooks/useOfficialUnitMapFilter';
import { useFilteredViewportLots } from '../Map/hooks/useFilteredViewportLots';
import { useParcelFilterScope } from '../Map/hooks/useParcelFilterScope';
import { useParcelTileValues } from '../Map/hooks/useParcelTileValues';
import { getDistinctRowValues } from '../Map/data/parcelFilterRowMatch';
import { matchesFilteredParcelFeature } from '../Map/utils/matchesFilteredParcelFeature';
import {
  countActiveListingFilters,
  buildListingPriceBandOptions,
  filterOwnedLotsByPanelFilters,
} from '../Map/utils/sharedListingFilters';
import { provinceHasOfficialUnitFilters } from '../Map/utils/lotFunctionalUnits';
import { isNlSearchEnabled } from '../services/nlSearchService';
import { LOT_STATUS_COLORS, MAP_MIN_LOT_ZOOM, OFFICIAL_UNIT_FILTER_MIN_ZOOM, REFERENCE_LAYERS_MIN_ZOOM, SHARED_LIST_SELECTED_LOT_HIGHLIGHT_STYLE } from '../Map/styles/mapTheme';
import { isInCordobaProvinceCoverage } from '../Map/data/cordobaProvinceCoverage';
import { useParcelHover } from '../Map/hooks/useParcelHover';
import { buildParcelHoverTooltipHtml, buildSharedListLotTooltipHtml } from '../Map/utils/parcelHoverTooltip';
import { buildListDetailHoverPopup } from '../Map/utils/sharedRecordChips';
import { useListPinsLayer } from '../Map/hooks/useListPinsLayer';
import { useViewportLotHighlightIds } from '../Map/hooks/useViewportLotHighlightIds';
import { useBuildingsLayer } from '../Map/hooks/useBuildingsLayer';
import { usePobrezaCronicaArgentinaLayer } from '../Map/hooks/usePobrezaCronicaArgentinaLayer';
import { useZonasInundablesLayer } from '../Map/hooks/useZonasInundablesLayer';
import { useShareReferencePlaces } from '../Map/hooks/useShareReferencePlaces';
import { REFERENCE_PLACES_LAYER_IDS } from '../Map/utils/referencePlaces';
import { useZonificacionUrbanaLayer } from '../Map/hooks/useZonificacionUrbanaLayer';
import { useLotDeepLink } from '../Map/hooks/useLotDeepLink';
import { fitMapBoundsToLots, fitMapToLotBounds, locateLotOnMap, prepareMapForFit } from '../Map/utils/fitMapBoundsToLots';
import { resolveViewProvince } from '../Map/utils/resolveViewProvince';
import MapHeader from '../Map/components/MapHeader/MapHeader';
import { MOBILE_SEARCH_BAR_BLOCK_HEIGHT } from '../Map/components/MapHeader/MapHeaderSearchFilters.styles';
import MapControls from '../Map/components/MapControls/MapControls';
import DockPanel from '../Map/components/DockPanel/DockPanel';
import RegistrosPanel from '../Map/components/RegistrosPanel/RegistrosPanel';
import UserLotListsPanel from '../Map/components/UserLotListsPanel/UserLotListsPanel';
import HistoryPanel from '../Map/components/HistoryPanel/HistoryPanel';
import LeadsPanel from '../Leads/components/LeadsPanel';
import MetaAdsPanel from '../MetaAds/components/MetaAdsPanel';
import { useMetaConnection } from '../MetaAds/hooks/useMetaConnection';
import { MetaAdsProvider } from '../MetaAds/MetaAdsContext';
import { META_ADS_PANEL_PATH, isMetaAdsEnabled } from '../MetaAds/metaAdsConfig';
import {
  buildLeadReturnOrigin,
  buildLeadRecordReturn,
  getLeadReturnLabel,
  isLeadReturnOriginInSpace,
} from '../Leads/leadsConfig';
import { useActiveSpace } from '../Map/context/ActiveSpaceContext';
import FilterResultsPanel from '../Map/components/FilterResultsPanel/FilterResultsPanel';
import AdministrativeBreadcrumb from '../Map/components/AdministrativeBreadcrumb/AdministrativeBreadcrumb';
import { useBrandsModal } from '../Components/BrandsModal/BrandsModalContext';
import MapStatusBanner from '../Map/components/MapStatusBanner/MapStatusBanner';
import MarketComparablesMode from '../MarketComparison/components/MarketComparablesMode';
import { shouldExitMarketComparables } from '../MarketComparison/marketComparablesMap';
import { mapStatusBannerSpinnerStyle } from '../Map/components/MapStatusBanner/MapStatusBanner.styles';
import { getDesktopPanelOccupiedWidth } from '../Map/components/PanelShell/PanelShell';
import { snapshotMapCamera, restoreMapCamera } from '../Map/utils/mapCameraSnapshot';
import { useCadParcelsLayer, CAD_FILL_LAYER_ID, queryCadOverlayAtPoint } from '../Showroom/hooks/useCadParcelsLayer';
import { useCadListFilters } from '../Showroom/hooks/useCadListFilters';
import {
  buildCadFilterGroups,
  getCadAppliedFilterChips,
  getCadSurfaceBandsForExtent,
} from '../Showroom/data/cadFilterContract';
import { NL_SEARCH_CAD_CONTEXT } from '../Map/data/nlSearchConstants';
import { useCadProjectsIndex } from '../Showroom/hooks/useCadProjectsIndex';
import { useCadGeorefStickerLayer } from '../Showroom/hooks/useCadGeorefStickerLayer';
import { useCadAlignFlatView } from '../Showroom/hooks/useCadAlignFlatView';
import { useCadAlignQuietMap } from '../Showroom/hooks/useCadAlignQuietMap';
import { useCadMapCanvasImage } from '../Showroom/hooks/useCadMapCanvasImage';
import { computeGeometryBbox, isCadMapDisplay } from '../Showroom/utils/cadGeometry';
import { shouldPaintCadMasterplan } from '../Showroom/utils/cadCanvasImage';
import { isCadListPinEvent, useCadListPinsLayer, CAD_LIST_PINS_LAYER_ID } from '../Showroom/hooks/useCadListPinsLayer';
import {
  isCadProjectPinEvent,
  useCadProjectPinsLayer,
} from '../Showroom/hooks/useCadProjectPinsLayer';
import { buildCadListMapPins, canPaintCadListProjectPin, jumpMapToCadListPreview, omitCadListPinsForProject } from '../Showroom/utils/cadOwnerMapOverlay';
import { readCadProjectParcelCount } from '../Showroom/utils/cadParcelDisplay';
import { getCadProjectId } from '../Showroom/utils/cadAccess';
import { toast } from '../Components/Toast';
import { queryCadRefLotBboxOnMap, readCadastralLotIdFromFeature } from '../Showroom/utils/cadGeoref';
import { canLocateCadParcelOnCountryMap, getCadParcelCardLabel } from '../Showroom/utils/cadRegistros';
import ShowroomPlanStage from '../Showroom/components/ShowroomPlanStage';
import '../Map/Map.css';
import { ProductTour } from '../onboarding/ProductTour';
import { buildTourSteps, filterStepsByDom } from '../onboarding/tourSteps';
import {
  useProductTour,
  FEEDBACK_HINT_SESSION_KEY,
  TOUR_LS_KEY,
} from '../hooks/useProductTour';
import { layout, colors } from '../styles/theme';
import { withAlpha } from '../Map/utils/sharedBranding';

const MOBILE_BREAKPOINT_PX = 768;

const PARCELS_ZOOM_PASSIVE_HINT_TEXT = 'Hacé zoom para ver los lotes.';
const PARCELS_ZOOM_TOGGLE_HINT_TEXT = 'Hacé zoom para ver u ocultar lotes.';
const MAP_LOADING_MESSAGE = 'Un momento, estamos cargando el mapa...';
const LIST_VIEWPORT_LOADING_MESSAGE = 'Ubicando lotes en el mapa...';

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const SIDEBAR_RAIL_WIDTH = layout.sidebarRailWidth;
/** Por encima de este umbral solo se usan pines (paint en PMTiles con N ids es muy costoso). */
const MY_LOTS_PARCEL_HIGHLIGHT_MAX = 250;

const CAD_STAGE_OFF = {
  enabled: false,
  mode: 'off',
  project: null,
  featureCollection: null,
  selectedId: null,
  onSelect: null,
  onPlanClick: null,
  onPinSelect: null,
  highlightedPinId: null,
  georefPick: null,
  pinPick: false,
  georefSticker: false,
  canvasImageSticker: null,
  fitNonce: '',
  onStickerTranslate: null,
  imageTransform: null,
  imagePaintUrl: null,
  onImageTransformChange: null,
  adjustImage: false,
};

function parcelFilterScopeKey(scope) {
  if (!scope || scope.type === 'all') return 'all';
  if (scope.type === 'myLots') return 'myLots';
  if (scope.type === 'cad') return `cad:${scope.projectId || scope.slug || ''}`;
  return `list:${scope.listId}`;
}

const DESKTOP_LIST_FIT_PADDING = { top: 100, right: 60, bottom: 60, left: 600 };
const MOBILE_LIST_FIT_PADDING = { top: 100, right: 24, bottom: 280, left: 24 };
const DESKTOP_LOT_FIT_PADDING = { top: 90, right: 60, bottom: 60, left: 460 };
const MOBILE_LOT_FIT_PADDING = { top: 90, right: 24, bottom: 280, left: 24 };

function getMapPageListFitPadding(isMobile) {
  return isMobile ? MOBILE_LIST_FIT_PADDING : DESKTOP_LIST_FIT_PADDING;
}

function getMapPageLotFitPadding(isMobile) {
  return isMobile ? MOBILE_LOT_FIT_PADDING : DESKTOP_LOT_FIT_PADDING;
}

const AUTH_FORM_PATHS = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/verify'];

const ZONIFICACION_URBANA_COVERAGE_MESSAGE =
  'Por el momento, la zonificación urbana no tiene cobertura en esta zona.';

function MapPage({ showControls = true, showStatusOverlays = true, showHeader = true }) {
  return (
    <div style={{ position: 'relative', height: '100vh', width: '100%', backgroundColor: '#0f0f0f' }}>
      <MapComponent
        showControls={showControls}
        showStatusOverlays={showStatusOverlays}
        showHeader={showHeader}
      />
    </div>
  );
}

const MapComponent = ({ showControls, showStatusOverlays, showHeader }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT_PX - 1}px)`);
  const isMapRoute = location.pathname === '/app';
  const { isLoggedIn, user, logout, isLoading: authLoading } = useAuth();
  const { activeOwnerUserId, spaceSwitchSeq, isViewingSharedSpace } = useActiveSpace();
  const { openBrands } = useBrandsModal();
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  const [deferFeedbackHint, setDeferFeedbackHint] = useState(false);
  const [feedbackHintTrigger, setFeedbackHintTrigger] = useState(0);
  const { selectedCompany } = useCompany();

  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;

  const [selectedPolygon, setSelectedPolygon] = useState(null);
  const {
    lotViewQuota,
    lotViewQuotaExceeded,
    applyLotViewQuotaFromLot,
  } = useLotViewQuota(isLoggedIn);
  const [mapView, setMapView] = useState('satellite1');
  const [showParcelasToggleZoomHint, setShowParcelasToggleZoomHint] = useState(false);
  const [parcelsZoomHintDismissed, setParcelsZoomHintDismissed] = useState(false);
  const [showParcelas, setShowParcelas] = useState(true);
  const [showPobrezaCronicaArgentina, setShowPobrezaCronicaArgentina] = useState(false);
  const [pobrezaCronicaArgentinaOpacity, setPobrezaCronicaArgentinaOpacity] = useState(0.34);
  const [showZonasInundables, setShowZonasInundables] = useState(false);
  const [showZonificacionUrbana, setShowZonificacionUrbana] = useState(false);
  const [zonificacionUrbanaCoverageMessage, setZonificacionUrbanaCoverageMessage] = useState('');
  const zonificacionUrbanaCoverageTimeoutRef = useRef(null);
  const zonificacionUrbanaLeavingCoverageRef = useRef(false);
  // Toggle global de edificios 3D por bbox (MapControls + getbuildingsbybounds) desactivado;
  // solo queda el sólido por lote vía DockPanel + /getbuilding.
  /** Vista edificio en mapa: full (3D+planta), footprints (solo contorno/planta), hidden (nada) */
  const [lotBuildingDisplayMode, setLotBuildingDisplayMode] = useState('hidden');
  /** Estado del fetch del edificio: 'idle' | 'loading' | 'available' | 'unavailable' */
  const [lotBuildingStatus, setLotBuildingStatus] = useState('idle');
  const [parcelasStateFilter, setParcelasStateFilter] = useState('');
  const { mapInfo, syncMapViewInfo, viewMap } = useMapViewInfo({ zoom: '', lat: '', lng: '' });
  const [locationMessage, setLocationMessage] = useState('');
  const [isListViewportLoading, setIsListViewportLoading] = useState(false);
  const [isLotLoading, setIsLotLoading] = useState(false);
  const [lotLoadError, setLotLoadError] = useState(null);
  const [isMyLotsOpen, setIsMyLotsOpen] = useState(false);
  const [isListsOpen, setIsListsOpen] = useState(false);
  /**
   * Comparación de mercado · modo Comparables (T3): `{ itemKind, recordId, ownerUserId, revision,
   * unitLabel, center, radiusM, months, operation, source: 'dock'|'lists', anchorId, seq }` o null.
   * Mientras dura, el Dock muestra la vista Comparables y se esconden los demás pines y chips.
   * Ver docs/maps/market-comparison.md («Modo Comparables»).
   */
  const [marketComparablesView, setMarketComparablesView] = useState(null);
  const marketModeOn = Boolean(marketComparablesView);
  const marketViewRef = useRef(null);
  marketViewRef.current = marketComparablesView;
  const marketCameraRef = useRef(null);
  const marketViewSeqRef = useRef(0);
  const [listFilterContext, setListFilterContext] = useState(null);
  const [registrosWorkingLots, setRegistrosWorkingLots] = useState(null);
  /** Ids ya recortados por publicación/UF; el tile no conoce esos campos. */
  const [listingMatchedScopeIds, setListingMatchedScopeIds] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  /** Panel de Leads. Ver docs/maps/leads.md. */
  const [isLeadsOpen, setIsLeadsOpen] = useState(false);
  /** Panel Meta Ads (flag + `getmetaconnection`). Ver docs/maps/meta-ads.md. */
  const [isMetaAdsOpen, setIsMetaAdsOpen] = useState(false);
  /** Vista con la que abre el panel: «Ver todas las campañas» desde la ficha (E4) abre en Campañas. */
  const [metaAdsInitialView, setMetaAdsInitialView] = useState('checklist');
  // «Ir a Campañas» desde la ficha: abre Campañas con esa campaña elegida (`seq` = cada pedido).
  const [metaAdsFocusCampaign, setMetaAdsFocusCampaign] = useState(null);
  const metaAdsEnabled = isLoggedIn && isMetaAdsEnabled();
  const metaConnection = useMetaConnection({
    ownerUserId: isViewingSharedSpace ? activeOwnerUserId : null,
    enabled: metaAdsEnabled,
  });
  // La entrada del menú aparece recién con la primera respuesta (sin parpadeo si la API da 404).
  const showMetaAdsNav = metaAdsEnabled && metaConnection.loaded && !metaConnection.hidden;
  /** Pedido one-shot para abrir una ficha de lead (bandeja de consultas, L2): `{ leadId, ts }`. */
  const [leadOpenRequest, setLeadOpenRequest] = useState(null);
  /**
   * Interés abierto desde la ficha de un lead (`buildLeadReturnOrigin`):
   * `{ leadId, leadName, ownerUserId }`. Mismo rol que `openedFromRegistros` /
   * `cadOpenedFromRegistros`: el Dock y Listas muestran “Volver a Lead: …” y
   * cerrarlos reabre esa ficha. Ver docs/maps/leads.md.
   */
  const [leadReturnOrigin, setLeadReturnOrigin] = useState(null);
  /** Lead abierto desde "Consultas y leads" de una UF o lote (`buildLeadRecordReturn`): su volver reabre ese registro. */
  const [leadUnitReturn, setLeadUnitReturn] = useState(null);
  /** Reabrir una UF sin modo edición (volver desde el lead). */
  const [pendingUserLotView, setPendingUserLotView] = useState(false);
  const [isFilterResultsOpen, setIsFilterResultsOpen] = useState(false);
  /** Pedido one-shot para abrir el detalle de una lista desde “Ver en lista”. */
  const [openListDetailRequest, setOpenListDetailRequest] = useState(null);
  const [openCadDetailRequest, setOpenCadDetailRequest] = useState(null);
  const [openDockInEditMode, setOpenDockInEditMode] = useState(false);
  const [pendingEditUserLotId, setPendingEditUserLotId] = useState(null);
  const [pendingOpenLotContent, setPendingOpenLotContent] = useState(false);
  const [openedFromRegistros, setOpenedFromRegistros] = useState(false);
  const [cadOpenedFromRegistros, setCadOpenedFromRegistros] = useState(false);
  const [cadProjectsReloadKey, setCadProjectsReloadKey] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [myLotsIds, setMyLotsIds] = useState([]);
  const [myLotsHighlightLots, setMyLotsHighlightLots] = useState([]);
  /** Lote ubicado desde Historial: se pinta con el mismo resaltado que el de Registros. */
  const [historyLocatedLotIds, setHistoryLocatedLotIds] = useState([]);
  const [filterResultsLocatedLotIds, setFilterResultsLocatedLotIds] = useState([]);
  const [listHighlightIds, setListHighlightIds] = useState([]);
  const [listHighlightLots, setListHighlightLots] = useState([]);
  /** Lote de la lista bajo el cursor del mapa (para marcar la card del detalle). */
  const [mapHoveredListLotId, setMapHoveredListLotId] = useState(null);
  /** Hover nacido en una card del detalle: resalta el lote y abre su publicación. */
  const [listCardHover, setListCardHover] = useState(null);
  /** Detalle de lista (propio o compartido) abierto: ahí el hover muestra la publicación. */
  const [listDetailHoverActive, setListDetailHoverActive] = useState(false);
  /** Lotes stagiados al agregar a lista (aún no confirmados): highlight verde primario. */
  const [listPendingAddIds, setListPendingAddIds] = useState([]);
  const [listPendingAddLots, setListPendingAddLots] = useState([]);
  /** Lote activo en detalle de lista compartida (solo mapa/edificio; el dock vive en UserLotListsPanel) */
  const [sharedListLotForMap, setSharedListLotForMap] = useState(null);
  /** Click en parcela del mapa perteneciente a la lista activa: lo resuelve UserLotListsPanel (dock con "volver"). */
  const [listLotMapClickRequest, setListLotMapClickRequest] = useState(null);
  /** Click en parcela durante modo "agregar desde mapa" en el panel de listas. */
  const [listMapAddPickRequest, setListMapAddPickRequest] = useState(null);
  const [cadGeorefPickRequest, setCadGeorefPickRequest] = useState(null);
  const [cadPinPlaceRequest, setCadPinPlaceRequest] = useState(null);
  const listAddPickModeRef = useRef({ active: false, stagedLotIds: [] });
  const [listAddPickActive, setListAddPickActive] = useState(false);
  const [cadOverlay, setCadOverlay] = useState(CAD_STAGE_OFF);
  const [cadOpenList, setCadOpenList] = useState(null);
  const [cadOverlayFitted, setCadOverlayFitted] = useState(false);
  const cadProjectsIndex = useCadProjectsIndex({
    enabled: isLoggedIn,
    reloadKey: cadProjectsReloadKey,
    ownerUserId: activeOwnerUserId,
  });
  const cadViewOpenRef = useRef(false);
  const cadCameraSnapshotRef = useRef(null);
  const cadParcelasBeforeRef = useRef(null);
  /** Plano canvas: restaurar cámara al Volver. Overlay en mapa (referenciado): quedarse. */
  const cadRestoreCameraOnLeaveRef = useRef(true);
  const cadPlanFitRef = useRef(null);
  const cadPreviewedListKeyRef = useRef('');
  const showParcelasRef = useRef(true);
  showParcelasRef.current = showParcelas;
  const [selectedProvinceName, setSelectedProvinceName] = useState(null);
  const [selectedDepartmentName, setSelectedDepartmentName] = useState(null);
  const [selectedPedaniaName, setSelectedPedaniaName] = useState(null);
  const [selectedMunicipalityName, setSelectedMunicipalityName] = useState(null);
  const [isAdministrativeMapLoading, setIsAdministrativeMapLoading] = useState(true);
  const favoriteIds = useMemo(() => favorites.map((f) => f.id), [favorites]);
  const listSelectedLotId = useMemo(() => {
    if (!isListsOpen || !sharedListLotForMap?.polygonInfo?.id) return null;
    return String(sharedListLotForMap.polygonInfo.id);
  }, [isListsOpen, sharedListLotForMap]);

  const selectedPolygonRef = useRef(selectedPolygon);
  selectedPolygonRef.current = selectedPolygon;
  const lotFetchGenerationRef = useRef(0);
  const filterPaintRef = useRef(null);
  const reapplyPointsFiltersRef = useRef(null);

  // Al abrir un lote (nuevo id): por defecto 3D + planta; sin lote: ocultar edificio en mapa.
  useEffect(() => {
    if (selectedPolygon?.id) {
      setLotBuildingDisplayMode('full');
      setLotBuildingStatus('idle');
    } else {
      setLotBuildingDisplayMode('hidden');
      setLotBuildingStatus('idle');
    }
  }, [selectedPolygon?.id]);
  const { consumeLotId } = useLotDeepLink();

  const {
    fetchPmtilesConfig,
    ensurePmtilesLayerForView,
    scheduleEnsurePmtilesLayerForView,
    toggleParcelas,
    applyParcelasFilter,
    getInteractiveParcelLayerIds,
    getParcelPointsLayerIds,
    getActiveParcelSources,
    syncParcelPointsView,
  } = usePmtilesLayers({
    getActivePaintExpressions: () => filterPaintRef.current,
    reapplyPointsFiltersRef,
  });

  const handleLocationError = (error, { silent }) => {
    if (!silent) setLocationMessage(error.message);
  };

  /**
   * Si "Mis listas" está abierto y el click cae sobre una parcela resaltada de la
   * lista activa, delega la apertura al panel (dock con contexto y flecha "volver")
   * en lugar del flujo genérico que cierra el panel y pierde el estado.
   * Ref para que el handler del mapa (bindeado una sola vez) lea valores frescos.
   */
  const interceptCadOwnerMapClickRef = useRef(null);
  const openCadParcelFromMapRef = useRef(null);
  const interceptListLotMapClickRef = useRef(null);
  interceptListLotMapClickRef.current = (event) => {
    const map = event?.target;
    if (!map || !isListsOpen) return false;
    if (isCadListPinEvent(event)) return true;
    // Pick modes antes que hit-test de pines: si no, un pin/borrador debajo
    // traga el click y no se puede reubicar al colocar.
    const georefPick = cadOverlay.georefPick;
    if (cadOverlay.pinPick) {
      const lng = Number(event?.lngLat?.lng);
      const lat = Number(event?.lngLat?.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return true;
      setCadPinPlaceRequest({ lng, lat, ts: Date.now() });
      return true;
    }
    if (georefPick === 'lnglat') {
      const lng = Number(event?.lngLat?.lng);
      const lat = Number(event?.lngLat?.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return true;
      setCadGeorefPickRequest({ kind: 'lnglat', lngLat: { lng, lat }, ts: Date.now() });
      return true;
    }
    if (isCadProjectPinEvent(event)) return true;
    if (queryCadOverlayAtPoint(map, event.point, [CAD_FILL_LAYER_ID]).length) return true;
    if (georefPick === 'lot') {
      if (map.getZoom() < MAP_MIN_LOT_ZOOM) return true;
      try {
        const cadHits = queryCadOverlayAtPoint(map, event.point, [CAD_FILL_LAYER_ID]);
        if (cadHits.length) return true;
        const layerIds = getInteractiveParcelLayerIds().filter((id) => map.getLayer(id));
        const feature = pickBestLotFeatureAtPoint(map, event.point, layerIds);
        const lotId = readCadastralLotIdFromFeature(feature);
        if (lotId) {
          const bbox = queryCadRefLotBboxOnMap(map, lotId, {
            layerIds,
            sources: getActiveParcelSources(map),
          }) || computeGeometryBbox(feature?.geometry);
          setCadGeorefPickRequest({
            kind: 'lot',
            lotId,
            bbox: bbox || null,
            ts: Date.now(),
          });
        }
      } catch (_) {
        // sin parcela bajo el cursor: seguimos interceptando para no abrir el dock
      }
      return true;
    }
    if (cadViewOpenRef.current || cadOverlay.mode === 'plan') return true;
    if (map.getZoom() < MAP_MIN_LOT_ZOOM) return false;
    try {
      const layerIds = getInteractiveParcelLayerIds().filter((id) => map.getLayer(id));
      const feature = pickBestLotFeatureAtPoint(map, event.point, layerIds);
      const lotId = getFeatureLotId(feature);
      if (!lotId) return false;
      const lotKey = String(lotId);

      // Agregar a lista (pick desde registros o mapa): no abrir detalle; stagear.
      const addPick = listAddPickModeRef.current;
      if (addPick?.active) {
        const lng = Number(event?.lngLat?.lng);
        const lat = Number(event?.lngLat?.lat);
        setListMapAddPickRequest({
          lotId: lotKey,
          ts: Date.now(),
          // Fallback de pin a zoom bajo si el lote no trae lat/lng en API.
          lngLat: Number.isFinite(lng) && Number.isFinite(lat) ? { lng, lat } : null,
        });
        return true;
      }

      if (listHighlightIds.length === 0) return false;
      if (!listHighlightIds.some((id) => String(id) === lotKey)) return false;
      const lotRow = listHighlightLots.find((l) => String(l?.lot_id) === lotKey) || null;
      setListLotMapClickRequest({ lotId: lotKey, lot: lotRow, ts: Date.now() });
      return true;
    } catch (_) {
      return false;
    }
  };

  interceptCadOwnerMapClickRef.current = (event) => {
    const map = event?.target;
    if (!map) return false;
    if (cadViewOpenRef.current || cadOverlay.georefPick) return false;
    try {
      if (queryCadOverlayAtPoint(map, event.point, [CAD_FILL_LAYER_ID]).length) return true;
    } catch (_) { /* estilo a medias */ }
    return false;
  };

  const hasOpenPanel = Boolean(
    isMyLotsOpen
    || isListsOpen
    || isHistoryOpen
    || isLeadsOpen
    || isMetaAdsOpen
    || isFilterResultsOpen
    || selectedPolygon
    || isLotLoading
    || lotLoadError
  );

  const hideMobileFeedbackButton = isMobile && !isNavDrawerOpen;

  const {
    mapRef,
    mapInstanceRef,
    mapUnavailable,
    mapUnavailableReason,
    setMapView: setMapViewOnMap,
    centerOnUserLocation,
    resetAdministrativeView,
    focusAdministrativeLevel,
  } = useMapInstance({
    surface: 'app',
    autoCenterOnLoad: false,
    onLoad: async (map) => {
      await fetchPmtilesConfig();
      ensurePmtilesLayerForView(map);
      syncMapViewInfo(map);

      const lotId = consumeLotId();
      if (lotId) fetchLotById(lotId);
    },
    onMove: (map) => {
      scheduleEnsurePmtilesLayerForView(map);
    },
    onMapClick: (event) => {
      if (event.originalEvent?.__cadParcelClick) return;
      if (isCadListPinEvent(event)) return;
      if (interceptCadOwnerMapClickRef.current?.(event)) return;
      if (interceptListLotMapClickRef.current?.(event)) return;
      if (cadViewOpenRef.current) return;
      if (listAddPickModeRef.current?.active) return;
      const map = event.target;
      if (!map || map.getZoom() < MAP_MIN_LOT_ZOOM) return;

      // Misma regla que el hover: sin parcela bajo el cursor no hay detalle ni GET.
      const layerIds = getInteractiveParcelLayerIds().filter((id) => map.getLayer(id));
      const lotId = getFeatureLotId(pickBestLotFeatureAtPoint(map, event.point, layerIds));
      if (!lotId) {
        if (selectedPolygonRef.current?.isLotPolygon) {
          clearLotPolygons();
          setSelectedPolygon(null);
          setLotLoadError(null);
        }
        return;
      }

      clearLotPolygons();
      if (selectedPolygonRef.current?.isLotPolygon) setSelectedPolygon(null);
      fetchLotById(lotId);
    },
    onLocationError: handleLocationError,
    onProvinceSelectionChange: setSelectedProvinceName,
    onDepartmentSelectionChange: setSelectedDepartmentName,
    onPedaniaSelectionChange: setSelectedPedaniaName,
    onMunicipalitySelectionChange: setSelectedMunicipalityName,
    onAdministrativeLoadChange: setIsAdministrativeMapLoading,
  });

  const { mapBearing, mapPitch, handleResetNorth, handleResetPitch } = useMapOrientation(
    mapInstanceRef,
    Boolean(mapInfo.zoom),
  );

  // Sin alcance activo, los barrios del desplegable salen de las parcelas
  // cargadas en la vista: no hay catálogo de barrios en el backend todavía.
  const {
    values: neighborhoodTileValues,
    refreshValues: refreshNeighborhoodTileValues,
  } = useParcelTileValues({
    mapInstanceRef,
    getActiveParcelSources,
    field: 'neighborhood_name',
  });

  useListPinsLayer({
    mapInstanceRef,
    lots: listHighlightLots,
    enabled: isListsOpen && listHighlightLots.length > 0 && !marketModeOn,
    idPrefix: 'list-pins',
  });

  // Pendientes de agregar: mismo mecanismo de pins que miembros de lista.
  useListPinsLayer({
    mapInstanceRef,
    lots: listPendingAddLots,
    enabled: isListsOpen && listPendingAddLots.length > 0 && !marketModeOn,
    idPrefix: 'list-pending-pins',
  });

  useListPinsLayer({
    mapInstanceRef,
    lots: selectedPolygon?.isLotPolygon ? [selectedPolygon] : [],
    enabled: !!selectedPolygon?.isLotPolygon && !marketModeOn,
    idPrefix: 'selected-lot-pin',
  });

  useListPinsLayer({
    mapInstanceRef,
    lots: myLotsHighlightLots,
    enabled: isMyLotsOpen && myLotsHighlightLots.length > 0 && !marketModeOn,
    idPrefix: 'my-lots-pins',
  });

  const enterCadView = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!cadCameraSnapshotRef.current) {
      cadCameraSnapshotRef.current = snapshotMapCamera(map);
      cadParcelasBeforeRef.current = showParcelasRef.current;
    }
    cadViewOpenRef.current = true;
  }, [mapInstanceRef]);

  const toggleParcelasRef = useRef(toggleParcelas);
  toggleParcelasRef.current = toggleParcelas;

  const leaveCadView = useCallback((options = {}) => {
    if (!cadViewOpenRef.current && !cadCameraSnapshotRef.current) return;
    const map = mapInstanceRef.current;
    const camera = cadCameraSnapshotRef.current;
    const parcelasBefore = cadParcelasBeforeRef.current;
    cadCameraSnapshotRef.current = null;
    cadParcelasBeforeRef.current = null;
    cadViewOpenRef.current = false;
    if (!options.retainOverlay) {
      setCadOverlay((prev) => (
        prev.mode === 'off' && !prev.enabled
          ? prev
          : CAD_STAGE_OFF
      ));
    }
    if (parcelasBefore != null && map) {
      toggleParcelasRef.current(map, parcelasBefore);
      setShowParcelas(parcelasBefore);
    }
    const restoreCamera = typeof options.restoreCamera === 'boolean'
      ? options.restoreCamera
      : cadRestoreCameraOnLeaveRef.current;
    if (restoreCamera && camera) restoreMapCamera(map, camera);
    cadRestoreCameraOnLeaveRef.current = true;
  }, [mapInstanceRef]);

  const handleCadViewChange = useCallback((payload) => {
    if (payload?.open) enterCadView();
    else {
      leaveCadView({
        retainOverlay: Boolean(payload?.retainOverlay),
        restoreCamera: payload?.restoreCamera,
      });
    }
  }, [enterCadView, leaveCadView]);

  const handleCadOverlayChange = useCallback((payload) => {
    const mode = payload?.mode || (payload?.enabled ? 'map' : 'off');
    if (mode === 'plan') cadRestoreCameraOnLeaveRef.current = true;
    else if (mode === 'map') cadRestoreCameraOnLeaveRef.current = false;
    setCadOverlay((prev) => {
      if (mode === 'plan') {
        const next = {
          ...CAD_STAGE_OFF,
          enabled: false,
          mode: 'plan',
          project: payload.project || null,
          featureCollection: payload.featureCollection || null,
          selectedId: payload.selectedId ?? null,
          onSelect: payload.onSelect || null,
          onPlanClick: payload.onPlanClick || null,
          onPinSelect: payload.onPinSelect || null,
          highlightedPinId: payload.highlightedPinId || null,
          pinPick: Boolean(payload.pinPick),
          imageTransform: payload.imageTransform || null,
          imagePaintUrl: payload.imagePaintUrl || null,
          onImageTransformChange: payload.onImageTransformChange || null,
          adjustImage: Boolean(payload.adjustImage),
        };
        if (
          prev.mode === next.mode
          && prev.project === next.project
          && prev.featureCollection === next.featureCollection
          && prev.selectedId === next.selectedId
          && prev.onSelect === next.onSelect
          && prev.onPlanClick === next.onPlanClick
          && prev.onPinSelect === next.onPinSelect
          && prev.highlightedPinId === next.highlightedPinId
          && prev.pinPick === next.pinPick
          && prev.imageTransform === next.imageTransform
          && prev.imagePaintUrl === next.imagePaintUrl
          && prev.onImageTransformChange === next.onImageTransformChange
          && prev.adjustImage === next.adjustImage
        ) {
          return prev;
        }
        return next;
      }
      if (mode === 'map' || payload?.enabled) {
        const next = {
          ...CAD_STAGE_OFF,
          enabled: true,
          mode: 'map',
          featureCollection: payload.featureCollection || null,
          selectedId: payload.selectedId ?? null,
          onSelect: payload.onSelect || null,
          onPinSelect: payload.onPinSelect || null,
          highlightedPinId: payload.highlightedPinId || null,
          project: payload.project || null,
          georefPick: payload?.georefPick || null,
          pinPick: Boolean(payload?.pinPick),
          georefSticker: Boolean(payload?.georefSticker),
          canvasImageSticker: payload.canvasImageSticker || null,
          fitNonce: payload?.fitNonce ?? '',
          onStickerTranslate: payload?.onStickerTranslate || null,
          imageTransform: payload.imageTransform || null,
          imagePaintUrl: payload.imagePaintUrl || null,
          onImageTransformChange: payload.onImageTransformChange || null,
          adjustImage: Boolean(payload.adjustImage),
        };
        if (
          prev.enabled === next.enabled
          && prev.mode === next.mode
          && prev.featureCollection === next.featureCollection
          && prev.selectedId === next.selectedId
          && prev.onSelect === next.onSelect
          && prev.onPinSelect === next.onPinSelect
          && prev.highlightedPinId === next.highlightedPinId
          && prev.project === next.project
          && prev.georefPick === next.georefPick
          && prev.pinPick === next.pinPick
          && prev.georefSticker === next.georefSticker
          && prev.canvasImageSticker === next.canvasImageSticker
          && prev.imageTransform === next.imageTransform
          && prev.imagePaintUrl === next.imagePaintUrl
          && prev.onImageTransformChange === next.onImageTransformChange
          && prev.adjustImage === next.adjustImage
          && prev.fitNonce === next.fitNonce
          && prev.onStickerTranslate === next.onStickerTranslate
        ) {
          return prev;
        }
        return next;
      }
      const next = {
        ...CAD_STAGE_OFF,
        georefPick: payload?.georefPick || null,
        pinPick: Boolean(payload?.pinPick),
      };
      if (
        prev.mode === next.mode
        && prev.enabled === next.enabled
        && prev.georefPick === next.georefPick
        && prev.pinPick === next.pinPick
        && prev.georefSticker === next.georefSticker
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const dropCadProjectPin = cadProjectsIndex.dropProject;
  const upsertCadProjectPin = cadProjectsIndex.upsertProject;
  const handleCadOwnerOverlayInvalidate = useCallback((project) => {
    if (project) dropCadProjectPin(project);
    setCadProjectsReloadKey((key) => key + 1);
  }, [dropCadProjectPin]);
  const handleCadProjectPinSync = useCallback((project) => {
    if (project) upsertCadProjectPin(project);
  }, [upsertCadProjectPin]);

  const handleListAddPickModeChange = useCallback((config) => {
    const next = config || { active: false, showTooltip: false, stagedLotIds: [] };
    listAddPickModeRef.current = next;
    setListAddPickActive(Boolean(next.showTooltip));
  }, []);

  const {
    scope: filterScope,
    scopeLots: filterScopeLots,
    isScopeActive: isFilterScopeActive,
    scopeLoading: filterScopeLoading,
    scopeError: filterScopeError,
    scopeLists: filterScopeLists,
    scopeCadLists: filterScopeCadLists,
    scopeListsLoading: filterScopeListsLoading,
    setScope: setFilterScope,
    clearScope: clearFilterScope,
    ensureScopeListsLoaded: ensureFilterScopeListsLoaded,
  } = useParcelFilterScope({ isLoggedIn, ownerUserId: activeOwnerUserId });

  /**
   * El detalle de una lista CAD viaja por el mismo canal, con `kind: 'cad'`.
   * No entra al alcance catastral: un `custom_parcels.id` no es un `lots.id`.
   * @see docs/maps/cad-list-filters.md
   */
  const cadFilterContext = listFilterContext?.kind === 'cad' ? listFilterContext : null;
  const isListDetailOpen = !cadFilterContext && Boolean(listFilterContext?.listId);
  const isFilterScopeLocked = isMyLotsOpen || isListDetailOpen || Boolean(cadFilterContext);
  const isCadFilterMode = Boolean(cadFilterContext) || filterScope?.type === 'cad';
  const cadListFilters = useCadListFilters();

  const cadFitPadding = useMemo(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT_PX;
    if (isMobile) {
      return { top: 88, bottom: 40, left: 24, right: 24 };
    }
    return {
      top: 80,
      bottom: 40,
      left: getDesktopPanelOccupiedWidth(SIDEBAR_RAIL_WIDTH) + 16,
      right: 40,
    };
  }, []);

  const cadPlanOpen = cadOverlay.mode === 'plan' && isListsOpen;
  const cadProjectOverlayOn = Boolean(cadOverlay.enabled && isListsOpen);
  const cadOpenListKey = cadOpenList && cadOpenList !== true
    ? String(cadOpenList.slug || cadOpenList.id || '')
    : (cadOpenList ? 'open' : '');
  const cadGeorefNeedsMap = Boolean(
    cadOverlay.georefPick || cadOverlay.georefSticker || cadOverlay.adjustImage,
  );
  // Sin mapa (WebGL) nunca llega el encuadre: no bloquear la pantalla esperándolo.
  const cadViewSettling = !mapUnavailable
    && Boolean(cadOpenList)
    && isListsOpen
    && !cadPlanOpen
    && !cadOverlayFitted
    && !cadGeorefNeedsMap;
  const cadListMapPins = useMemo(
    () => omitCadListPinsForProject(
      buildCadListMapPins(cadProjectsIndex.projects),
      cadOpenList && cadOpenList !== true ? cadOpenList : null,
    ),
    [cadOpenList, cadProjectsIndex.projects],
  );
  const cadAlignQuiet = Boolean(cadProjectOverlayOn && cadOverlay.adjustImage);
  const cadHideTerrioTiles = Boolean(
    cadProjectOverlayOn && (cadOverlay.georefSticker || cadOverlay.adjustImage)
  );
  // enabled ≠ “hay pines visibles”: al abrir la única lista el pin se
  // omite y si apagamos el hook MapLibre saca el symbol a mitad de
  // placement (continuePlacement / reading 'get'). Se oculta con
  // `visible`, no se desarma.
  const cadListPinsEnabled = Boolean(
    isLoggedIn && cadProjectsIndex.projects.some(canPaintCadListProjectPin),
  );
  const cadListPinsVisible = Boolean(
    !cadOpenList
    && !cadPlanOpen
    && !cadOverlay.georefSticker
    && !cadProjectOverlayOn
    && !marketModeOn
  );

  useEffect(() => {
    setCadOverlayFitted(false);
    cadPreviewedListKeyRef.current = '';
  }, [cadOpenListKey]);

  useEffect(() => {
    if (!cadOpenList || cadOpenList === true || cadPlanOpen) return;
    if (!isCadMapDisplay(cadOpenList)) return;
    const key = cadOpenListKey;
    if (!key || cadPreviewedListKeyRef.current === key) return;
    const map = mapInstanceRef.current;
    if (jumpMapToCadListPreview(map, cadOpenList, { padding: cadFitPadding })) {
      cadPreviewedListKeyRef.current = key;
    }
  }, [cadFitPadding, cadOpenList, cadOpenListKey, cadPlanOpen, mapInstanceRef]);
  // Los polígonos de todas las listas mezclaban el loteo con el catastro.
  // En el mapa país van pines por lista; los lotes se pintan al abrir una.
  const openCadListFromMapRef = useRef(null);
  useCadListPinsLayer({
    mapInstanceRef,
    pins: cadListMapPins,
    enabled: cadListPinsEnabled,
    visible: cadListPinsVisible,
    onSelect: (list) => openCadListFromMapRef.current?.(list),
  });

  useCadMapCanvasImage({
    mapInstanceRef,
    enabled: cadProjectOverlayOn,
    project: cadOverlay.project,
    sticker: cadOverlay.canvasImageSticker,
    transform: cadOverlay.imageTransform,
    paintUrl: cadOverlay.imagePaintUrl,
    onTransformChange: cadOverlay.onImageTransformChange,
    adjustEnabled: cadOverlay.adjustImage,
  });

  const { locateParcel, fitAllParcels: fitCadParcels } = useCadParcelsLayer({
    mapInstanceRef,
    enabled: cadProjectOverlayOn,
    featureCollection: cadOverlay.featureCollection,
    selectedId: cadOverlay.selectedId,
    onSelect: cadOverlay.onSelect,
    fitPadding: cadFitPadding,
    fitOnLoad: cadProjectOverlayOn,
    pickEnabled: !cadOverlay.georefSticker && !cadOverlay.pinPick,
    pickContext: true,
    fitNonce: cadOverlay.fitNonce,
    minZoom: MAP_MIN_LOT_ZOOM,
    alignMode: cadOverlay.adjustImage,
    masterplanEnabled: shouldPaintCadMasterplan(cadOverlay.project, {
      alignMode: Boolean(cadOverlay.adjustImage),
    }),
    initialFitDuration: 0,
    onInitialFit: () => setCadOverlayFitted(true),
  });

  useCadGeorefStickerLayer({
    mapInstanceRef,
    // Mientras se alinea el render, el arrastre es de la ilustración.
    enabled: Boolean(isListsOpen && cadOverlay.georefSticker && !cadOverlay.adjustImage),
    onTranslate: cadOverlay.onStickerTranslate,
  });

  useCadAlignQuietMap({
    mapInstanceRef,
    enabled: cadAlignQuiet,
  });

  useCadAlignFlatView({
    mapInstanceRef,
    enabled: cadAlignQuiet,
  });

  const cadProjectOverlayOnRef = useRef(false);
  useEffect(() => {
    if (cadProjectOverlayOnRef.current && !cadProjectOverlayOn) {
      setCadProjectsReloadKey((key) => key + 1);
    }
    cadProjectOverlayOnRef.current = cadProjectOverlayOn;
  }, [cadProjectOverlayOn]);

  useEffect(() => {
    if (cadOverlay.georefPick !== 'lot' && cadOverlay.georefPick !== 'lnglat' && !cadOverlay.pinPick) {
      return undefined;
    }
    if (cadOverlay.georefSticker) return undefined;
    const map = mapInstanceRef.current;
    if (!map) return undefined;
    if (cadOverlay.georefPick === 'lot' || cadOverlay.georefPick === 'lnglat') {
      if (!showParcelasRef.current) {
        toggleParcelasRef.current(map, true);
        setShowParcelas(true);
      }
    }
    const canvas = map.getCanvas();
    const container = typeof map.getContainer === 'function' ? map.getContainer() : null;
    const setCrosshair = () => {
      try { canvas.style.cursor = 'crosshair'; } catch (_) { /* noop */ }
    };
    setCrosshair();
    // Clase con !important: hover de admin/parcelas pisa el style inline.
    try { container?.classList.add('terrio-map-crosshair'); } catch (_) { /* noop */ }
    map.on('mousemove', setCrosshair);
    map.on('mouseenter', setCrosshair);
    return () => {
      map.off('mousemove', setCrosshair);
      map.off('mouseenter', setCrosshair);
      try { container?.classList.remove('terrio-map-crosshair'); } catch (_) { /* noop */ }
      try { canvas.style.cursor = ''; } catch (_) { /* noop */ }
    };
  }, [cadOverlay.georefPick, cadOverlay.georefSticker, cadOverlay.pinPick, mapInstanceRef]);

  useCadProjectPinsLayer({
    mapInstanceRef,
    project: cadOverlay.project,
    featureCollection: cadOverlay.featureCollection,
    paintMode: 'map',
    enabled: Boolean(cadProjectOverlayOn && !cadPlanOpen),
    // Modo Comparables: se ocultan (no se desarman; mismo motivo que los pines de listas).
    visible: !marketModeOn,
    interactive: !cadOverlay.pinPick && !cadOverlay.georefSticker && !cadOverlay.adjustImage,
    onSelectPin: cadOverlay.onPinSelect,
    highlightedPinId: cadOverlay.highlightedPinId,
  });

  useEffect(() => {
    if (cadPlanOpen || !cadOverlay.enabled) return;
    if (cadOverlay.selectedId) locateParcel(cadOverlay.selectedId);
  }, [cadOverlay.enabled, cadOverlay.selectedId, cadPlanOpen, locateParcel]);

  const handleCadFitInView = useCallback(() => {
    if (cadOverlay.mode === 'plan') {
      cadPlanFitRef.current?.();
      return;
    }
    fitCadParcels();
  }, [cadOverlay.mode, fitCadParcels]);

  // El lote abierto en el dock del mapa manda. El de la lista solo si ese dock
  // sigue siendo el de la lista: si no, un registro anterior deja el edificio pegado.
  const activeLotForBuildings = selectedPolygon
    ?? (isListsOpen ? sharedListLotForMap?.polygonInfo ?? null : null);

  useBuildingsLayer({
    mapInstanceRef,
    enabled: lotBuildingDisplayMode !== 'hidden' && !cadAlignQuiet,
    buildingView: lotBuildingDisplayMode,
    mode: 'all',
    selectedLotGeometry: activeLotForBuildings?.geometry ?? null,
    filterLotId: lotBuildingDisplayMode !== 'hidden' ? (activeLotForBuildings?.id ?? null) : null,
    onBuildingStatusChange: setLotBuildingStatus,
  });

  usePobrezaCronicaArgentinaLayer({
    mapInstanceRef,
    enabled: showPobrezaCronicaArgentina,
    opacity: pobrezaCronicaArgentinaOpacity,
  });

  useZonasInundablesLayer({
    mapInstanceRef,
    enabled: showZonasInundables,
  });

  const { mapControlProps: referencePlacesControlProps } = useShareReferencePlaces({
    mapInstanceRef,
    enabled: true,
    interactive: isLoggedIn,
    preferTiles: true,
    surface: 'app',
  });

  useZonificacionUrbanaLayer({
    mapInstanceRef,
    enabled: showZonificacionUrbana,
  });

  const useViewportMyLotsPaint = (
    isMyLotsOpen
    && myLotsHighlightLots.length > 0
    && myLotsIds.length > MY_LOTS_PARCEL_HIGHLIGHT_MAX
  );
  const myLotsViewportIds = useViewportLotHighlightIds({
    mapInstanceRef,
    lots: myLotsHighlightLots,
    enabled: useViewportMyLotsPaint,
    maxCount: MY_LOTS_PARCEL_HIGHLIGHT_MAX,
  });
  const myLotsIdsForPaint = useMemo(() => {
    if (myLotsIds.length === 0) return [];
    if (myLotsIds.length <= MY_LOTS_PARCEL_HIGHLIGHT_MAX) return myLotsIds;
    return myLotsViewportIds;
  }, [myLotsIds, myLotsViewportIds]);

  // Un solo canal de resaltado «Registros» (`myLotsIds` en useParcelFilters):
  // con Historial o Resultados abierto (y Registros / Listas cerrados) pinta el
  // lote ubicado ahí. Nunca dos a la vez; al cerrar el panel se limpia (efectos
  // de abajo). Resultados no pinta mientras está oculto por la ficha de un lote.
  const filterResultsPanelHidden = Boolean(selectedPolygon) || isLotLoading || Boolean(lotLoadError);
  const historyLocatedPaintActive = isHistoryOpen && !isMyLotsOpen && historyLocatedLotIds.length > 0;
  const filterResultsLocatedPaintActive = isFilterResultsOpen
    && !filterResultsPanelHidden
    && !isMyLotsOpen
    && !isListsOpen
    && !historyLocatedPaintActive
    && filterResultsLocatedLotIds.length > 0;
  let registrosPaintIds = myLotsIdsForPaint;
  if (historyLocatedPaintActive) registrosPaintIds = historyLocatedLotIds;
  else if (filterResultsLocatedPaintActive) registrosPaintIds = filterResultsLocatedLotIds;
  const registrosPaintActive = historyLocatedPaintActive
    || filterResultsLocatedPaintActive
    || (isMyLotsOpen && myLotsIdsForPaint.length > 0);

  useEffect(() => {
    if (!isHistoryOpen) setHistoryLocatedLotIds((prev) => (prev.length === 0 ? prev : []));
  }, [isHistoryOpen]);

  useEffect(() => {
    if (!isFilterResultsOpen) setFilterResultsLocatedLotIds((prev) => (prev.length === 0 ? prev : []));
  }, [isFilterResultsOpen]);

  const scopeBeforePanelRef = useRef(null);

  useEffect(() => {
    if (!isMyLotsOpen) setRegistrosWorkingLots(null);
  }, [isMyLotsOpen]);

  const effectiveScopeLots = isMyLotsOpen && Array.isArray(registrosWorkingLots)
    ? registrosWorkingLots
    : filterScopeLots;
  const effectiveScopeIds = useMemo(
    () => [...new Set(effectiveScopeLots.map((row) => row?.lot_id).filter(Boolean).map(String))],
    [effectiveScopeLots],
  );
  const paintScopeIds = listingMatchedScopeIds ?? effectiveScopeIds;

  /**
   * Registros o el detalle de una lista fijan el alcance: el mapa y el listado
   * hablan del mismo universo. Al salir se restaura el alcance previo.
   */
  useEffect(() => {
    if (!isLoggedIn) {
      scopeBeforePanelRef.current = null;
      return undefined;
    }

    const desiredScope = isMyLotsOpen
      ? { type: 'myLots' }
      : isListDetailOpen
        ? {
          type: 'list',
          listId: listFilterContext.listId,
          listName: listFilterContext.listName,
        }
        : cadFilterContext
          ? {
            type: 'cad',
            projectId: cadFilterContext.projectId,
            slug: cadFilterContext.slug,
            listName: cadFilterContext.listName,
          }
          : null;

    if (desiredScope) {
      if (scopeBeforePanelRef.current == null) {
        scopeBeforePanelRef.current = filterScope;
      }
      if (parcelFilterScopeKey(filterScope) !== parcelFilterScopeKey(desiredScope)) {
        setFilterScope(desiredScope);
      }
      return undefined;
    }

    const previous = scopeBeforePanelRef.current;
    if (previous == null) return undefined;
    scopeBeforePanelRef.current = null;

    if (parcelFilterScopeKey(previous) !== parcelFilterScopeKey(filterScope)) {
      setFilterScope(previous);
    }
    return undefined;
  }, [
    isMyLotsOpen,
    isListDetailOpen,
    isLoggedIn,
    listFilterContext,
    cadFilterContext,
    filterScope,
    setFilterScope,
  ]);

  /**
   * Opciones dinámicas del panel. Con alcance activo los barrios salen de las
   * filas de DB —lista completa del universo del usuario, sin depender del zoom—
   * y si no, de las parcelas cargadas en la vista.
   */
  const mapFilterOptions = useMemo(() => {
    const scopeValues = isFilterScopeActive
      ? getDistinctRowValues(effectiveScopeLots, 'neighborhood_name')
      : [];
    const values = isFilterScopeActive ? scopeValues : neighborhoodTileValues;
    const scopeLabel = filterScope?.type === 'list'
      ? 'la lista'
      : (isMyLotsOpen && Array.isArray(registrosWorkingLots) ? 'el listado' : 'tus registros');

    return {
      neighborhood_name: {
        options: values.map((value) => ({ key: value, value, label: value })),
        hint: isFilterScopeActive
          ? `Barrios de ${scopeLabel}`
          : 'Barrios de la vista actual',
        emptyHint: isFilterScopeActive
          ? `Los lotes de ${scopeLabel} no tienen barrio cargado`
          : 'Acercate al mapa, o filtrá sobre tus registros',
      },
    };
  }, [isFilterScopeActive, effectiveScopeLots, filterScope?.type, isMyLotsOpen, registrosWorkingLots, neighborhoodTileValues]);

  const handleRequestMapFilterOptions = useCallback((field) => {
    // Con alcance activo las opciones ya salen de las filas: no hay que leer tiles.
    if (field === 'neighborhood_name' && !isFilterScopeActive) {
      refreshNeighborhoodTileValues();
    }
  }, [isFilterScopeActive, refreshNeighborhoodTileValues]);

  const [officialUnitLotIds, setOfficialUnitLotIds] = useState(null);

  const keepListLotsWhenParcelasHidden = isListsOpen
    && (listHighlightIds.length > 0 || listPendingAddIds.length > 0);
  const clipCadastralToList = !showParcelas && keepListLotsWhenParcelasHidden;

  const {
    activeFilters,
    activeCount: activeFilterCount,
    matchExpression,
    showOnlyFiltered,
    setFilterValue,
    removeFilterChip,
    clearAllFilters,
    applyLotFiltersFromParse,
    toggleShowOnlyFiltered,
    getCurrentPaintExpressions,
  } = useParcelFilters({
    mapInstanceRef,
    getInteractiveParcelLayerIds,
    getParcelPointsLayerIds,
    syncParcelPointsView,
    reapplyPointsFiltersRef,
    myLotsIds: registrosPaintIds,
    isMyLotsOpen: registrosPaintActive,
    listHighlightIds,
    isListsOpen,
    listSelectedLotId,
    listPendingAddIds,
    listSelectedLotHighlightStyle: isListsOpen ? SHARED_LIST_SELECTED_LOT_HIGHLIGHT_STYLE : null,
    scopeIds: paintScopeIds,
    isScopeActive: isFilterScopeActive,
    scopeLockedByPanel: isFilterScopeLocked,
    officialUnitFiltersEnabled:
      !selectedProvinceName || provinceHasOfficialUnitFilters(selectedProvinceName),
    officialUnitLotIds,
    clipToHighlightIds: clipCadastralToList,
  });

  const officialUnitFilter = useOfficialUnitMapFilter({
    mapInstanceRef,
    activeFilters,
    enabled: !selectedProvinceName || provinceHasOfficialUnitFilters(selectedProvinceName),
    province: selectedProvinceName,
    onLotIdsChange: setOfficialUnitLotIds,
    onQuotaInfo: applyLotViewQuotaFromLot,
  });

  // Mismo criterio que `shouldApplyScope` en useParcelFilters: con el panel de
  // registros/lista abierto el mapa no se recorta hasta que haya filtros.
  const applyFilterScopeOnMap = isFilterScopeActive
    && !(isFilterScopeLocked && activeFilterCount === 0);

  const filterHoverScopeIdSet = useMemo(() => {
    if (!applyFilterScopeOnMap) return null;
    return new Set(paintScopeIds.map(String));
  }, [applyFilterScopeOnMap, paintScopeIds]);

  const filterHoverOfficialUnitIdSet = useMemo(() => {
    if (!Array.isArray(officialUnitLotIds)) return null;
    return new Set(officialUnitLotIds.map(String));
  }, [officialUnitLotIds]);

  const paintedHighlightIdSet = useMemo(() => {
    const ids = [];
    if (registrosPaintActive) {
      registrosPaintIds.forEach((id) => ids.push(String(id)));
    }
    if (isListsOpen) {
      listHighlightIds.forEach((id) => ids.push(String(id)));
      listPendingAddIds.forEach((id) => ids.push(String(id)));
    }
    return ids.length > 0 ? new Set(ids) : null;
  }, [registrosPaintActive, registrosPaintIds, isListsOpen, listHighlightIds, listPendingAddIds]);

  const canShowFilteredParcelTooltip = useCallback((feature) => {
    if (listAddPickActive) return true;
    if (paintedHighlightIdSet) {
      const lotId = getFeatureLotId(feature);
      if (lotId && paintedHighlightIdSet.has(String(lotId))) return true;
    }
    if (activeFilterCount === 0 && !applyFilterScopeOnMap) return false;
    return matchesFilteredParcelFeature(feature, {
      filters: activeFilters,
      scopeIdSet: filterHoverScopeIdSet,
      officialUnitLotIdSet: filterHoverOfficialUnitIdSet,
    });
  }, [
    listAddPickActive,
    paintedHighlightIdSet,
    activeFilterCount,
    applyFilterScopeOnMap,
    activeFilters,
    filterHoverScopeIdSet,
    filterHoverOfficialUnitIdSet,
  ]);

  const listHighlightLotsRef = useRef(listHighlightLots);
  listHighlightLotsRef.current = listHighlightLots;
  const listHighlightIdSetRef = useRef(new Set());
  listHighlightIdSetRef.current = new Set((listHighlightIds || []).map(String));
  const listDetailHoverActiveRef = useRef(false);
  listDetailHoverActiveRef.current = listDetailHoverActive;

  useEffect(() => {
    if (listDetailHoverActive) return;
    setMapHoveredListLotId(null);
    setListCardHover(null);
  }, [listDetailHoverActive]);

  const buildMapParcelTooltip = useCallback((properties, feature) => {
    const lotId = getFeatureLotId(feature);
    const lot = listDetailHoverActiveRef.current && lotId != null
      ? listHighlightLotsRef.current.find((row) => String(row?.lot_id) === String(lotId))
      : null;
    if (lot) {
      const html = buildSharedListLotTooltipHtml(lot);
      if (html) return { html, variant: 'share' };
    }
    return buildParcelHoverTooltipHtml(properties, feature);
  }, []);

  const handleMapParcelHover = useCallback((lotId) => {
    if (!listDetailHoverActiveRef.current) {
      setMapHoveredListLotId((prev) => (prev == null ? prev : null));
      return;
    }
    const inList = lotId != null && listHighlightIdSetRef.current?.has(String(lotId));
    const next = inList ? String(lotId) : null;
    setMapHoveredListLotId((prev) => (prev === next ? prev : next));
  }, []);

  const handleListRecordHover = useCallback((payload) => {
    if (!payload?.lotId) {
      setListCardHover(null);
      return;
    }
    const lot = listHighlightLotsRef.current.find((row) => String(row?.lot_id) === String(payload.lotId))
      || payload.item?.lot
      || null;
    const rich = payload.item ? buildListDetailHoverPopup(payload.item, lot) : null;
    const point = rich?.lngLat ? null : getLotRepresentativeLatLng(lot);
    const html = rich?.html || buildSharedListLotTooltipHtml(lot);
    const lngLat = rich?.lngLat || (point ? [point.lng, point.lat] : null);
    setListCardHover(html ? { lotId: String(payload.lotId), html, lngLat } : { lotId: String(payload.lotId), html: null, lngLat: null });
  }, []);

  // Hover de parcelas (PMTiles) también en modo público; no requiere login.
  // Tooltip: agregar desde el mapa, o polígonos pintados (filtros / alcance / panel).
  // En el detalle de lista, el lote pintado muestra la publicación (no el tile).
  useParcelHover({
    mapInstanceRef,
    getInteractiveParcelLayerIds,
    enabled: (showParcelas || clipCadastralToList)
      && !cadOverlay.enabled
      && cadOverlay.georefPick !== 'lnglat',
    showTooltip: listAddPickActive
      || activeFilterCount > 0
      || applyFilterScopeOnMap
      || Boolean(paintedHighlightIdSet),
    canShowTooltip: canShowFilteredParcelTooltip,
    buildTooltipHtml: buildMapParcelTooltip,
    onHoverLotId: handleMapParcelHover,
    externalHoverLotId: listCardHover?.lotId || null,
    externalHoverPopup: listCardHover?.html && listCardHover.lngLat
      ? { html: listCardHover.html, lngLat: listCardHover.lngLat }
      : null,
    occludingLayerIds: [...REFERENCE_PLACES_LAYER_IDS, CAD_LIST_PINS_LAYER_ID],
  });

  const handleSetFilterValue = useCallback((field, value) => {
    if (lotViewQuotaExceeded) return;
    if (field === 'listing_currency') setFilterValue('listing_price', null);
    setFilterValue(field, value);
  }, [lotViewQuotaExceeded, setFilterValue]);

  const cadScopeIndexProject = useMemo(() => {
    if (filterScope?.type !== 'cad') return null;
    const projectId = filterScope.projectId || filterScope.id;
    const slug = String(filterScope.slug || '').trim();
    return cadProjectsIndex.projects.find((project) => (
      (projectId != null && String(project.id || '') === String(projectId))
      || (slug && String(project.slug || '') === slug)
    )) || null;
  }, [filterScope, cadProjectsIndex.projects]);
  const cadFilterGroups = useMemo(
    () => buildCadFilterGroups(
      getCadSurfaceBandsForExtent(cadFilterContext?.areaExtent || null),
      // El estado se elige en el detalle (dropdown con conteos), no acá.
      { omitFields: ['stock_status'] },
    ),
    [cadFilterContext?.areaExtent],
  );
  const { clearAllFilters: clearCadListFilters } = cadListFilters;
  const cadScopeProjectId = cadFilterContext?.projectId
    || (filterScope?.type === 'cad' ? (filterScope.projectId || filterScope.slug) : null);
  useEffect(() => {
    if (!cadScopeProjectId) {
      clearCadListFilters();
      return;
    }
    // El panel pasa a cad-list-v1: los chips de catastro no se ven y no
    // pueden seguir recortando el mapa de fondo.
    clearAllFilters();
    setIsFilterResultsOpen(false);
  }, [cadScopeProjectId, clearCadListFilters, clearAllFilters]);

  const filterScopeSelectLists = useMemo(() => {
    const cadastral = filterScopeLists.map((list) => ({
      id: list.id,
      name: list.name,
      kind: 'list',
    }));
    const cad = filterScopeCadLists.map((project) => ({
      id: getCadProjectId(project) || project.slug,
      name: project.name || project.slug,
      kind: 'cad',
      projectId: getCadProjectId(project),
      slug: project.slug,
    }));
    return [...cadastral, ...cad].sort((a, b) => (
      String(a.name || '').localeCompare(String(b.name || ''), 'es')
    ));
  }, [filterScopeLists, filterScopeCadLists]);

  // Props del buscador cuando manda una lista CAD (abierta o elegida en alcance).
  const cadHeaderFilterProps = isCadFilterMode ? {
    activeFilters: cadListFilters.activeFilters,
    activeFilterCount: cadListFilters.activeFilterCount,
    onSetFilterValue: cadListFilters.setFilterValue,
    onRemoveFilterChip: cadListFilters.removeFilterChip,
    onClearAllFilters: cadListFilters.clearAllFilters,
    onApplyParsedLotFilters: cadListFilters.applyParsedFilters,
    parseSearchContext: NL_SEARCH_CAD_CONTEXT,
    filterGroups: cadFilterGroups,
    resolveAppliedChips: getCadAppliedFilterChips,
    hideFilterTabs: true,
    hideShowOnlyFiltered: true,
    skipPlaceSearch: true,
    showListingFilters: false,
    showOfficialUnitFilters: false,
    onOpenFilterResultsList: undefined,
    showOnlyFiltered: false,
    filterScopeCount: readCadProjectParcelCount(cadScopeIndexProject) ?? 0,
  } : null;

  const panelFilterOptions = useMemo(() => {
    if (!isFilterScopeActive) return mapFilterOptions;
    const selectedListingCurrencies = Array.isArray(activeFilters.listing_currency)
      ? activeFilters.listing_currency
      : [];
    const priceBandCurrency = selectedListingCurrencies.length === 1
      ? selectedListingCurrencies[0]
      : null;
    const scopeLabel = filterScope?.type === 'list'
      ? 'la lista'
      : (isMyLotsOpen && Array.isArray(registrosWorkingLots) ? 'el listado' : 'tus registros');
    return {
      ...mapFilterOptions,
      listing_price: {
        options: priceBandCurrency
          ? buildListingPriceBandOptions(effectiveScopeLots, priceBandCurrency)
          : [],
        hint: priceBandCurrency ? `Tramos en ${priceBandCurrency} según ${scopeLabel}` : null,
        emptyHint: priceBandCurrency ? null : 'Elegí una moneda para filtrar por precio',
      },
    };
  }, [
    mapFilterOptions,
    isFilterScopeActive,
    activeFilters,
    effectiveScopeLots,
    filterScope?.type,
    isMyLotsOpen,
    registrosWorkingLots,
  ]);

  const {
    lots: filterViewportLots,
    truncated: filterViewportTruncated,
    isSampleView: filterViewportIsSample,
    isRefreshing: filterViewportRefreshing,
    hasSettled: filterViewportSettled,
  } = useFilteredViewportLots({
    mapInstanceRef,
    getInteractiveParcelLayerIds,
    getParcelPointsLayerIds,
    matchExpression,
    enabled: isFilterResultsOpen && Boolean(matchExpression),
  });

  useEffect(() => {
    if (isFilterResultsOpen && activeFilterCount === 0) {
      setIsFilterResultsOpen(false);
    }
  }, [isFilterResultsOpen, activeFilterCount]);

  useEffect(() => {
    if (isFilterResultsOpen && (isMyLotsOpen || isListDetailOpen || isFilterScopeActive)) {
      setIsFilterResultsOpen(false);
    }
  }, [isFilterResultsOpen, isMyLotsOpen, isListDetailOpen, isFilterScopeActive]);

  useEffect(() => {
    if (!isListsOpen) {
      setOpenListDetailRequest(null);
      setOpenCadDetailRequest(null);
      setCadOpenedFromRegistros(false);
      setSharedListLotForMap(null);
    }
  }, [isListsOpen]);

  /** "Limpiar" resetea atributos. Con un panel que fija el alcance, ese se mantiene. */
  const handleClearAllFilters = useCallback(() => {
    if (lotViewQuotaExceeded) return;
    clearAllFilters();
    if (!isFilterScopeLocked) clearFilterScope();
  }, [lotViewQuotaExceeded, clearAllFilters, clearFilterScope, isFilterScopeLocked]);

  const handleRemoveFilterChip = useCallback((chip) => {
    if (lotViewQuotaExceeded) return;
    removeFilterChip(chip);
  }, [lotViewQuotaExceeded, removeFilterChip]);

  const handleToggleShowOnlyFiltered = useCallback(() => {
    if (lotViewQuotaExceeded) return;
    toggleShowOnlyFiltered();
  }, [lotViewQuotaExceeded, toggleShowOnlyFiltered]);

  const handleApplyParsedLotFilters = useCallback((filters) => {
    if (lotViewQuotaExceeded) return;
    applyLotFiltersFromParse(filters);
  }, [lotViewQuotaExceeded, applyLotFiltersFromParse]);

  const pendingCadastralScopeFitRef = useRef(false);

  const fitFilterScopeToMap = useCallback((scope) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const fitOptions = {
      padding: getMapPageListFitPadding(isMobile),
      maxZoom: 16.5,
      duration: 900,
    };
    if (scope?.type === 'cad') {
      const pin = cadListMapPins.find((item) => (
        (scope.projectId != null && String(item.id || '') === String(scope.projectId))
        || (scope.slug && String(item.slug || '') === String(scope.slug))
      ));
      if (!pin) {
        toast.info('Esta lista no tiene ubicación en el mapa.');
        return;
      }
      map.flyTo({
        center: [pin.lng, pin.lat],
        zoom: 14,
        duration: fitOptions.duration,
      });
      return;
    }
    if (scope?.type === 'list') {
      const didFit = fitMapBoundsToLots(map, filterScopeLots, fitOptions);
      if (!didFit) toast.info('Esta lista no tiene lotes con ubicación.');
    }
  }, [cadListMapPins, filterScopeLots, isMobile, mapInstanceRef]);

  const handleSetFilterScope = useCallback((nextScope) => {
    if (lotViewQuotaExceeded || isFilterScopeLocked) return;
    pendingCadastralScopeFitRef.current = nextScope?.type === 'list';
    setFilterScope(nextScope);
    if (nextScope?.type === 'cad') fitFilterScopeToMap(nextScope);
  }, [lotViewQuotaExceeded, isFilterScopeLocked, setFilterScope, fitFilterScopeToMap]);

  useEffect(() => {
    if (!pendingCadastralScopeFitRef.current) return;
    if (filterScope?.type !== 'list' || filterScopeLoading) return;
    pendingCadastralScopeFitRef.current = false;
    fitFilterScopeToMap(filterScope);
  }, [filterScope, filterScopeLoading, fitFilterScopeToMap]);

  useEffect(() => {
    if (!lotViewQuotaExceeded) return;
    clearAllFilters();
    if (!isFilterScopeLocked) clearFilterScope();
    setIsFilterResultsOpen(false);
  }, [lotViewQuotaExceeded, clearAllFilters, clearFilterScope, isFilterScopeLocked]);

  // Los filtros se evalúan contra `filter_attributes` de la DB: a zoom bajo los
  // tiles de puntos son una muestra y omiten la mayoría de las parcelas de una
  // lista, así que los pins salen de getmylots y se filtran acá.
  const filterScopeMatchedLots = useMemo(() => (
    isFilterScopeActive ? filterOwnedLotsByPanelFilters(effectiveScopeLots, activeFilters) : []
  ), [isFilterScopeActive, effectiveScopeLots, activeFilters]);

  const listingFilterCount = countActiveListingFilters(activeFilters);
  const filterResultsLots = useMemo(() => {
    if (!isFilterScopeActive || listingFilterCount === 0) return filterViewportLots;
    const allowed = new Set(filterScopeMatchedLots.map((row) => String(row?.lot_id)).filter(Boolean));
    return filterViewportLots.filter((lot) => allowed.has(String(lot.lot_id)));
  }, [isFilterScopeActive, listingFilterCount, filterScopeMatchedLots, filterViewportLots]);

  useEffect(() => {
    if (!isFilterScopeActive || listingFilterCount === 0) {
      setListingMatchedScopeIds((prev) => (prev == null ? prev : null));
      return;
    }
    const next = [...new Set(
      filterScopeMatchedLots.map((row) => String(row?.lot_id)).filter(Boolean),
    )];
    setListingMatchedScopeIds((prev) => {
      if (
        Array.isArray(prev)
        && prev.length === next.length
        && prev.every((id, i) => id === next[i])
      ) {
        return prev;
      }
      return next;
    });
  }, [isFilterScopeActive, listingFilterCount, filterScopeMatchedLots]);

  useListPinsLayer({
    mapInstanceRef,
    lots: filterScopeMatchedLots,
    // Con un panel que fija el alcance, los pins salen del listado filtrado.
    enabled: isFilterScopeActive && !isFilterScopeLocked && filterScopeMatchedLots.length > 0 && !marketModeOn,
    idPrefix: 'filter-scope-pins',
  });

  // El alcance elegido en el panel cuenta en el badge; el bloqueo por un panel no.
  const headerFilterCount = activeFilterCount + (isFilterScopeActive && !isFilterScopeLocked ? 1 : 0);

  filterPaintRef.current = getCurrentPaintExpressions();

  const {
    clearLotPolygons,
    fetchLotById: baseFetchLotById,
    fetchLotByIdReturningData,
  } = useLotSelection({
    mapInstanceRef,
    setSelectedPolygon,
    onQuotaInfo: applyLotViewQuotaFromLot,
    uiLeftInset:
      isLoggedIn
        ? SIDEBAR_RAIL_WIDTH
        : 0,
  });

  const fetchLotById = async (id, {
    keepRegistrosOpen = false,
    keepFilterResultsOpen = false,
    leadReturnOrigin: nextLeadReturnOrigin = null,
  } = {}) => {
    const fetchGeneration = ++lotFetchGenerationRef.current;
    // Solo la apertura desde un lead conserva el volver; cualquier otra lo limpia.
    setLeadReturnOrigin(nextLeadReturnOrigin);
    if (!keepRegistrosOpen) {
      setIsMyLotsOpen(false);
      setOpenedFromRegistros(false);
    }
    if (!keepFilterResultsOpen) {
      setIsFilterResultsOpen(false);
    }
    setIsListsOpen(false);
    setIsHistoryOpen(false);
    setIsLotLoading(true);
    setLotLoadError(null);
    try {
      const lotData = await baseFetchLotById(id);
      if (fetchGeneration !== lotFetchGenerationRef.current) return;
      if (isLotViewQuotaBlockedPayload(lotData)) {
        setLotLoadError(buildLotViewQuotaExceededMessage(lotData.quota?.resetsAt));
        return;
      }
      if (!lotData) {
        setLotLoadError('No se encontró información para este lote.');
        return;
      }
      if (id && isLoggedInRef.current) trackLotView({ lot_id: id }).catch(() => {});
    } catch (error) {
      if (fetchGeneration !== lotFetchGenerationRef.current) return;
      console.error('Error al cargar lote por ID:', error);
      setLotLoadError('Ocurrió un error al cargar la información del lote.');
    } finally {
      if (fetchGeneration === lotFetchGenerationRef.current) {
        setIsLotLoading(false);
      }
    }
  };

  const handleSelectMapView = async (viewId) => {
    const applied = await setMapViewOnMap(viewId);
    if (applied) setMapView(viewId);
  };

  const handleToggleParcelas = () => {
    const map = mapInstanceRef.current;
    const zoom = map?.getZoom();
    if (map && Number.isFinite(zoom) && zoom < MAP_MIN_LOT_ZOOM) {
      setShowParcelasToggleZoomHint(true);
    }
    const next = !showParcelas;
    setShowParcelas(next);
    if (cadHideTerrioTiles) {
      toggleParcelas(map, false);
      return;
    }
    toggleParcelas(map, next || keepListLotsWhenParcelasHidden);
  };

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (cadHideTerrioTiles) {
      toggleParcelasRef.current(map, false);
      return;
    }
    toggleParcelasRef.current(map, showParcelas || keepListLotsWhenParcelasHidden);
  }, [cadHideTerrioTiles, showParcelas, keepListLotsWhenParcelasHidden, mapInstanceRef]);

  const debouncedFilter = useRef(debounce((val) => applyParcelasFilter(mapInstanceRef.current, val), 300));
  const handleParcelasFilterChange = useCallback((val) => {
    setParcelasStateFilter(val);
    debouncedFilter.current(val);
  }, []);

  const handleCenterOnUserLocation = async () => {
    const result = await centerOnUserLocation();
    if (result.success) setLocationMessage('');
  };

  useEffect(() => {
    if (!locationMessage) return undefined;
    const timeoutId = setTimeout(() => setLocationMessage(''), 5000);
    return () => clearTimeout(timeoutId);
  }, [locationMessage]);

  useEffect(() => {
    if (!showParcelasToggleZoomHint) return undefined;
    const timeoutId = setTimeout(() => setShowParcelasToggleZoomHint(false), 5000);
    return () => clearTimeout(timeoutId);
  }, [showParcelasToggleZoomHint]);

  useEffect(() => {
    const zoom = Number.parseFloat(mapInfo.zoom);
    const inPassiveHintRange = (
      !isAdministrativeMapLoading
      && !isListViewportLoading
      && !cadViewSettling
      && Number.isFinite(zoom)
      && zoom >= 10
      && zoom < 14
    );
    if (!inPassiveHintRange) {
      setParcelsZoomHintDismissed(false);
    }
  }, [mapInfo.zoom, isAdministrativeMapLoading, isListViewportLoading, cadViewSettling]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const controller = new AbortController();
    getFavorites(controller.signal)
      .then((res) => { if (res.data) setFavorites(res.data); })
      .catch((e) => { if (e?.name !== 'AbortError') console.error('Error loading favorites:', e); });
    return () => controller.abort();
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) return;
    setShowPobrezaCronicaArgentina(false);
    setShowZonasInundables(false);
    setShowZonificacionUrbana(false);
    setZonificacionUrbanaCoverageMessage('');
  }, [isLoggedIn]);

  useEffect(() => () => {
    if (zonificacionUrbanaCoverageTimeoutRef.current) {
      clearTimeout(zonificacionUrbanaCoverageTimeoutRef.current);
      zonificacionUrbanaCoverageTimeoutRef.current = null;
    }
  }, []);

  const showZonificacionUrbanaCoverageHint = Boolean(
    showStatusOverlays && zonificacionUrbanaCoverageMessage,
  );

  const notifyZonificacionUrbanaOutsideCoverage = useCallback(() => {
    setZonificacionUrbanaCoverageMessage(ZONIFICACION_URBANA_COVERAGE_MESSAGE);
    if (zonificacionUrbanaCoverageTimeoutRef.current) {
      clearTimeout(zonificacionUrbanaCoverageTimeoutRef.current);
    }
    zonificacionUrbanaCoverageTimeoutRef.current = setTimeout(() => {
      setZonificacionUrbanaCoverageMessage('');
      zonificacionUrbanaCoverageTimeoutRef.current = null;
    }, 6000);
  }, []);

  const handleDismissZonificacionUrbanaCoverageHint = useCallback(() => {
    setZonificacionUrbanaCoverageMessage('');
    if (zonificacionUrbanaCoverageTimeoutRef.current) {
      clearTimeout(zonificacionUrbanaCoverageTimeoutRef.current);
      zonificacionUrbanaCoverageTimeoutRef.current = null;
    }
  }, []);

  const canEnableZonificacionUrbanaAtCenter = useCallback(() => {
    const zoom = Number.parseFloat(mapInfo.zoom);
    const map = mapInstanceRef.current;
    const center = map?.getCenter();

    if (
      !Number.isFinite(zoom)
      || zoom < REFERENCE_LAYERS_MIN_ZOOM.zonificacionUrbana
      || !center
    ) {
      return true;
    }

    return isInCordobaProvinceCoverage([center.lng, center.lat]);
  }, [mapInfo.zoom, mapInstanceRef]);

  const handleToggleZonificacionUrbana = useCallback(() => {
    if (!isLoggedIn) return;

    if (!showZonificacionUrbana && !canEnableZonificacionUrbanaAtCenter()) {
      notifyZonificacionUrbanaOutsideCoverage();
      return;
    }

    zonificacionUrbanaLeavingCoverageRef.current = false;
    setZonificacionUrbanaCoverageMessage('');
    setShowZonificacionUrbana((prev) => !prev);
  }, [
    isLoggedIn,
    showZonificacionUrbana,
    canEnableZonificacionUrbanaAtCenter,
    notifyZonificacionUrbanaOutsideCoverage,
  ]);

  // Si la capa está activa y el usuario sale de la provincia (zoom suficiente), apagarla.
  useEffect(() => {
    if (!showZonificacionUrbana || !isLoggedIn) {
      zonificacionUrbanaLeavingCoverageRef.current = false;
      return;
    }

    const zoom = Number.parseFloat(mapInfo.zoom);
    const lng = Number.parseFloat(mapInfo.lng);
    const lat = Number.parseFloat(mapInfo.lat);

    if (
      !Number.isFinite(zoom)
      || zoom < REFERENCE_LAYERS_MIN_ZOOM.zonificacionUrbana
      || !Number.isFinite(lng)
      || !Number.isFinite(lat)
    ) {
      return;
    }

    if (isInCordobaProvinceCoverage([lng, lat])) {
      zonificacionUrbanaLeavingCoverageRef.current = false;
      return;
    }

    if (zonificacionUrbanaLeavingCoverageRef.current) return;
    zonificacionUrbanaLeavingCoverageRef.current = true;

    setShowZonificacionUrbana(false);
    notifyZonificacionUrbanaOutsideCoverage();
  }, [
    mapInfo.lat,
    mapInfo.lng,
    mapInfo.zoom,
    showZonificacionUrbana,
    isLoggedIn,
    notifyZonificacionUrbanaOutsideCoverage,
  ]);

  useEffect(() => {
    // Si no está abierto "Registros" ni "Listas", no debe quedar ningún resaltado activo.
    if (!isMyLotsOpen && !isListsOpen) {
      setMyLotsIds([]);
      setMyLotsHighlightLots([]);
      setListHighlightIds([]);
      setListPendingAddIds([]);
      setListPendingAddLots([]);
    }
  }, [isMyLotsOpen, isListsOpen]);

  useEffect(() => {
    // Regla UX: al abrir "Mis listas" el mapa arranca sin resaltado.
    // El resaltado se aplicará recién cuando el usuario elija "Mostrar lotes en el mapa".
    const keepIfEmpty = (prev) => (prev.length === 0 ? prev : []);
    if (isListsOpen) {
      setMyLotsIds(keepIfEmpty);
      return;
    }
    leaveCadView();
    setListHighlightIds(keepIfEmpty);
    setListHighlightLots(keepIfEmpty);
    setListPendingAddIds(keepIfEmpty);
    setListPendingAddLots(keepIfEmpty);
    setListAddPickActive(false);
    setIsListViewportLoading(false);
  }, [isListsOpen, leaveCadView]);

  useEffect(() => {
    if (!isListsOpen || listHighlightLots.length === 0) {
      setIsListViewportLoading(false);
      return undefined;
    }

    const map = mapInstanceRef.current;
    if (!map) return undefined;

    setIsListViewportLoading(true);
    const didFit = fitMapBoundsToLots(map, listHighlightLots, {
      padding: getMapPageListFitPadding(isMobile),
      maxZoom: 16.5,
      duration: 900,
    });

    if (!didFit) {
      setIsListViewportLoading(false);
      return undefined;
    }

    let finished = false;
    let timeoutId = null;

    const finishLoading = () => {
      if (finished) return;
      finished = true;
      if (timeoutId) clearTimeout(timeoutId);
      map.off('idle', handleIdle);
      setIsListViewportLoading(false);
    };

    const handleIdle = () => {
      finishLoading();
    };

    map.on('idle', handleIdle);
    timeoutId = setTimeout(finishLoading, 3000);

    return () => {
      finishLoading();
    };
  }, [isListsOpen, isMobile, listHighlightLots, mapInstanceRef]);

  const finishMapViewportLoading = useCallback((map, setLoading) => {
    let finished = false;
    let timeoutId = null;
    const finishLoading = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timeoutId);
      map.off('idle', finishLoading);
      setLoading(false);
    };
    map.on('idle', finishLoading);
    timeoutId = setTimeout(finishLoading, 3000);
  }, []);

  const handleFitMyLotsInView = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || myLotsHighlightLots.length === 0) return;
    fitMapBoundsToLots(map, myLotsHighlightLots, {
      padding: getMapPageListFitPadding(isMobile),
      maxZoom: 16.5,
      duration: 900,
    });
  }, [isMobile, myLotsHighlightLots, mapInstanceRef]);

  const handleFitListsInView = useCallback(
    (lotsOverride) => {
      const map = mapInstanceRef.current;
      const lots =
        Array.isArray(lotsOverride) && lotsOverride.length > 0 ? lotsOverride : listHighlightLots;
      if (!map || lots.length === 0) return;
      setIsListViewportLoading(true);
      const didFit = fitMapBoundsToLots(map, lots, {
        padding: getMapPageListFitPadding(isMobile),
        maxZoom: 16.5,
        duration: 900,
      });
      if (!didFit) {
        setIsListViewportLoading(false);
        return;
      }
      finishMapViewportLoading(map, setIsListViewportLoading);
    },
    [finishMapViewportLoading, isMobile, listHighlightLots, mapInstanceRef]
  );

  const handleTourCheckResolved = useCallback(({ showTour }) => {
    if (showTour) {
      setDeferFeedbackHint(true);
      return;
    }
    setDeferFeedbackHint(false);
    setFeedbackHintTrigger((n) => n + 1);
  }, []);

  const handleTourFinished = useCallback(() => {
    setDeferFeedbackHint(false);
    setIsNavDrawerOpen(false);
    try {
      sessionStorage.setItem(FEEDBACK_HINT_SESSION_KEY, '1');
    } catch (_) {
      // ignore
    }
    setFeedbackHintTrigger((n) => n + 1);
  }, []);

  const openNavDrawer = useCallback(() => setIsNavDrawerOpen(true), []);
  const closeNavDrawer = useCallback(() => setIsNavDrawerOpen(false), []);

  const tourSteps = useMemo(
    () => buildTourSteps({
      isMobile,
      openNavDrawer,
      closeNavDrawer,
      hiddenTargets: [
        ...(isViewingSharedSpace ? ['[data-tour="historial"]'] : []),
        ...(!showMetaAdsNav ? ['[data-tour="meta-ads"]'] : []),
      ],
    }),
    [isMobile, openNavDrawer, closeNavDrawer, isViewingSharedSpace, showMetaAdsNav]
  );

  const { run: tourRun, handleCallback: handleTourCallback, restartTour } = useProductTour({
    isLoggedIn,
    authLoading,
    isMapRoute,
    closeNavDrawer,
    onTourFinished: handleTourFinished,
    onTourCheckResolved: handleTourCheckResolved,
  });

  // Snapshot de steps al arrancar el tour: descarta targets que no están montados.
  const [activeTourSteps, setActiveTourSteps] = useState(null);
  useEffect(() => {
    if (!tourRun) {
      setActiveTourSteps(null);
      return;
    }
    setActiveTourSteps(filterStepsByDom(tourSteps, { isMobile }));
    // Solo al arrancar: cambiar steps a mitad del tour desincroniza Joyride.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRun]);

  /**
   * Limpia la UI del usuario (capas, filtros, paneles, resaltados) al quedar
   * deslogueado. La usan el logout manual y la sesión vencida.
   */
  const resetLoggedOutMapUi = () => {
    setShowZonasInundables(false);
    setShowZonificacionUrbana(false);
    setZonificacionUrbanaCoverageMessage('');
    setMapView('satellite1');
    setMapViewOnMap('satellite1');
    if (!showParcelas && mapInstanceRef.current) {
      setShowParcelas(true);
      toggleParcelas(mapInstanceRef.current, true);
    }
    clearLotPolygons();
    clearAllFilters();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setIsMyLotsOpen(false);
    setIsListsOpen(false);
    setIsHistoryOpen(false);
    setIsLeadsOpen(false);
    setIsMetaAdsOpen(false);
    setLeadReturnOrigin(null);
    setIsFilterResultsOpen(false);
    setFavorites([]);
    setMyLotsIds([]);
    setMyLotsHighlightLots([]);
    setListHighlightIds([]);
    setListHighlightLots([]);
    setListPendingAddIds([]);
    setListPendingAddLots([]);
    setSelectedProvinceName(null);
    setSelectedDepartmentName(null);
    setSelectedPedaniaName(null);
    setSelectedMunicipalityName(null);
  };

  const resetLoggedOutMapUiRef = useRef(resetLoggedOutMapUi);
  resetLoggedOutMapUiRef.current = resetLoggedOutMapUi;
  const manualLogoutResetRef = useRef(false);
  const wasLoggedInRef = useRef(isLoggedIn);

  // Sesión vencida (refresh token muerto) o logout en otra pestaña: AuthContext
  // pasa a deslogueado sin pasar por handleLogout. Misma limpieza, sin navegar.
  useEffect(() => {
    const wasLoggedIn = wasLoggedInRef.current;
    wasLoggedInRef.current = isLoggedIn;
    if (!wasLoggedIn || isLoggedIn) return;
    if (manualLogoutResetRef.current) {
      manualLogoutResetRef.current = false;
      return;
    }
    resetLoggedOutMapUiRef.current();
  }, [isLoggedIn]);

  const handleLogout = async () => {
    try {
      localStorage.removeItem(TOUR_LS_KEY);
    } catch (_) {
      // ignore
    }
    manualLogoutResetRef.current = true;
    await logout();
    resetLoggedOutMapUi();
    navigate('/app');
  };

  const handleClearAdministrativeSelection = () => {
    resetAdministrativeView();
    setSelectedProvinceName(null);
    setSelectedDepartmentName(null);
    setSelectedPedaniaName(null);
    setSelectedMunicipalityName(null);
  };

  const handleFocusAdministrativeLevel = (level) => {
    const focused = focusAdministrativeLevel(level);
    if (!focused) return;
    if (level === 'province') {
      setSelectedDepartmentName(null);
      setSelectedPedaniaName(null);
      setSelectedMunicipalityName(null);
    } else if (level === 'department') {
      setSelectedPedaniaName(null);
      setSelectedMunicipalityName(null);
    } else if (level === 'pedania') {
      setSelectedMunicipalityName(null);
    }
  };

  const handleZoomIn = () => mapInstanceRef.current?.zoomIn();
  const handleZoomOut = () => mapInstanceRef.current?.zoomOut();

  const resolveViewProvinceForParse = useCallback(() => resolveViewProvince({
    selectedProvinceName,
    map: mapInstanceRef.current,
  }), [selectedProvinceName, mapInstanceRef]);

  const handleFlyTo = ({ lng, lat, bbox, revealFiltered }) => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (revealFiltered) {
      if (map.getZoom() > 12) map.easeTo({ zoom: 10, duration: 900 });
      return;
    }
    if (bbox) map.fitBounds(bbox, { padding: 60, duration: 1200, maxZoom: 17 });
    else map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 15), duration: 1200 });
  };

  const handleCloseDockPanel = useCallback(() => {
    clearLotPolygons();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setLotBuildingDisplayMode('hidden');
    setPendingEditUserLotId(null);
    setPendingUserLotView(false);
    setPendingOpenLotContent(false);
    setOpenDockInEditMode(false);
    setOpenedFromRegistros(false);
    setLeadReturnOrigin(null);
  }, [clearLotPolygons]);

  const handleBackToRegistros = useCallback(() => {
    clearLotPolygons();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setLotBuildingDisplayMode('hidden');
    setPendingEditUserLotId(null);
    setPendingOpenLotContent(false);
    setOpenDockInEditMode(false);
    setOpenedFromRegistros(false);
    setIsMyLotsOpen(true);
  }, [clearLotPolygons]);

  const handleInitialOpenLotContentConsumed = useCallback(() => {
    setPendingOpenLotContent(false);
  }, []);

  /**
   * Cambio de espacio (chip / drawer): nada del espacio anterior queda abierto.
   * Mismo cierre que el onClose de Listas + Registros + Dock. Al desmontarse,
   * los paneles limpian su selección (lista / CAD / filtro de lista) y los
   * efectos de isListsOpen/isMyLotsOpen apagan resaltados y pines.
   * Resultados de filtros no son del espacio: quedan. Historial es personal:
   * se oculta en espacio ajeno (efecto de abajo), en el propio queda.
   */
  const resetSpaceScopedUi = useCallback(() => {
    // Modo Comparables: es del registro del espacio anterior (sin volver a su encuadre).
    marketCameraRef.current = null;
    setMarketComparablesView(null);
    setSharedListLotForMap(null);
    setOpenListDetailRequest(null);
    setOpenCadDetailRequest(null);
    setCadOpenedFromRegistros(false);
    setLeadReturnOrigin(null);
    leaveCadView();
    // Overlay retenido (plano/referenciado) también es del espacio anterior.
    setCadOverlay(CAD_STAGE_OFF);
    setIsListsOpen(false);
    setIsMyLotsOpen(false);
    setIsLeadsOpen(false);
    setIsMetaAdsOpen(false);
    handleCloseDockPanel();
    setListLotMapClickRequest(null);
    setListMapAddPickRequest(null);
    setCadGeorefPickRequest(null);
    setCadPinPlaceRequest(null);
    handleListAddPickModeChange(null);
  }, [leaveCadView, handleCloseDockPanel, handleListAddPickModeChange]);

  // Solo en un cambio real (no al montar ni al restaurar el espacio guardado).
  const handledSpaceSwitchSeqRef = useRef(spaceSwitchSeq);
  useEffect(() => {
    if (handledSpaceSwitchSeqRef.current === spaceSwitchSeq) return;
    handledSpaceSwitchSeqRef.current = spaceSwitchSeq;
    resetSpaceScopedUi();
  }, [spaceSwitchSeq, resetSpaceScopedUi]);

  // Historial = vistas del usuario logueado: no existe en espacio ajeno.
  // Cubre entrar al espacio y el restore desde sessionStorage.
  useEffect(() => {
    if (isViewingSharedSpace) setIsHistoryOpen(false);
  }, [isViewingSharedSpace]);

  const handleSharedLotDockChange = useCallback((payload) => {
    setSharedListLotForMap(payload);
    if (!payload) {
      setLotBuildingDisplayMode('hidden');
      setLotBuildingStatus('idle');
    }
  }, []);

  useEffect(() => {
    if (!AUTH_FORM_PATHS.includes(location.pathname)) return;
    handleCloseDockPanel();
  }, [location.pathname, handleCloseDockPanel]);

  useEffect(() => {
    if (!AUTH_FORM_PATHS.includes(location.pathname)) return;
    if (!selectedPolygon && !isLotLoading && !lotLoadError) return;
    navigate('/app', { replace: true });
  }, [location.pathname, selectedPolygon, isLotLoading, lotLoadError, navigate]);

  const handleUpdatePolygon = (updatedInfo) => {
    if (updatedInfo.polygon?.layerId && mapInstanceRef.current) {
      const hasCompany = updatedInfo.companies?.some((c) => c.id === selectedCompany?.id);
      const color = hasCompany
        ? LOT_STATUS_COLORS.linkedToSelectedCompany
        : LOT_STATUS_COLORS.notLinkedToSelectedCompany;
      const map = mapInstanceRef.current;

      if (map.getLayer(updatedInfo.polygon.layerId)) {
        map.setPaintProperty(updatedInfo.polygon.layerId, 'fill-color', color);
        map.setPaintProperty(updatedInfo.polygon.layerId, 'fill-outline-color', color);
      }
      if (map.getLayer(`${updatedInfo.polygon.layerId}-border`)) {
        map.setPaintProperty(`${updatedInfo.polygon.layerId}-border`, 'line-color', color);
      }
    }
    setSelectedPolygon(updatedInfo);
  };

  const locateLotSeqRef = useRef(0);

  const handleLocateLot = useCallback(async (lotId, lotRow = null) => {
    if (!lotId) return;
    const map = mapInstanceRef.current;
    if (!map) return;

    const seq = ++locateLotSeqRef.current;
    const fromList = listHighlightLotsRef.current.find(
      (lot) => String(lot?.lot_id) === String(lotId),
    );

    await locateLotOnMap(map, {
      lotId,
      candidates: [lotRow, fromList],
      fetchLotById: fetchLotByIdReturningData,
      fitOptions: {
        padding: getMapPageLotFitPadding(isMobile),
        duration: 700,
        pointZoom: 17,
      },
      isCancelled: () => seq !== locateLotSeqRef.current,
    });
  }, [isMobile, mapInstanceRef, fetchLotByIdReturningData]);

  const handleLocateCadParcel = useCallback(async (parcel) => {
    if (!canLocateCadParcelOnCountryMap(parcel)) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    await prepareMapForFit(map);
    fitMapToLotBounds(map, { lat: parcel.lat, lng: parcel.lng }, {
      padding: getMapPageLotFitPadding(isMobile),
      duration: 700,
      pointZoom: 17,
    });
  }, [isMobile, mapInstanceRef]);

  const handleOpenCadParcel = useCallback((parcel) => {
    const slug = String(parcel?.project_slug || '').trim();
    if (!slug) return;
    clearLotPolygons();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setOpenedFromRegistros(false);
    setCadOpenedFromRegistros(true);
    setLeadReturnOrigin(null);
    setIsMyLotsOpen(true);
    setIsHistoryOpen(false);
    setIsFilterResultsOpen(false);
    setMyLotsIds([]);
    setOpenCadDetailRequest({
      slug,
      parcelId: parcel.id ? String(parcel.id) : null,
      parcelLabel: getCadParcelCardLabel(parcel),
      project: {
        slug,
        name: parcel.project_name || slug,
        id: parcel.project_id || null,
        display_mode: parcel.display_mode || null,
        can_manage: parcel.can_manage === true,
        ...(parcel.can_publish === true ? { can_publish: true } : {}),
      },
      ts: Date.now(),
    });
    setIsListsOpen(true);
  }, [clearLotPolygons]);

  const handleOpenCadListFromMap = useCallback((list) => {
    const slug = String(list?.slug || '').trim();
    if (!slug) return;
    clearLotPolygons();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setOpenedFromRegistros(false);
    setCadOpenedFromRegistros(false);
    setLeadReturnOrigin(null);
    setIsMyLotsOpen(false);
    setIsHistoryOpen(false);
    setIsFilterResultsOpen(false);
    setOpenCadDetailRequest({
      slug,
      parcelId: null,
      parcelLabel: null,
      project: {
        slug,
        name: list.name || slug,
        id: list.id || null,
        display_mode: list.display_mode || 'map',
        can_manage: list.can_manage === true,
        ...(list.can_publish === true ? { can_publish: true } : {}),
        ...(Number.isFinite(Number(list.lat)) ? { lat: Number(list.lat) } : {}),
        ...(Number.isFinite(Number(list.lng)) ? { lng: Number(list.lng) } : {}),
      },
      ts: Date.now(),
    });
    setIsListsOpen(true);
  }, [clearLotPolygons]);
  openCadListFromMapRef.current = handleOpenCadListFromMap;

  const handleOpenCadParcelFromMap = useCallback((parcel) => {
    const slug = String(parcel?.project_slug || '').trim();
    if (!slug) return;
    clearLotPolygons();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setOpenedFromRegistros(false);
    setCadOpenedFromRegistros(false);
    setLeadReturnOrigin(null);
    setIsMyLotsOpen(false);
    setIsHistoryOpen(false);
    setIsFilterResultsOpen(false);
    setOpenCadDetailRequest({
      slug,
      parcelId: parcel.id ? String(parcel.id) : null,
      parcelLabel: getCadParcelCardLabel(parcel),
      project: {
        slug,
        name: parcel.project_name || slug,
        id: parcel.project_id || null,
        display_mode: parcel.display_mode || null,
        can_manage: parcel.can_manage === true,
        ...(parcel.can_publish === true ? { can_publish: true } : {}),
      },
      ts: Date.now(),
    });
    setIsListsOpen(true);
  }, [clearLotPolygons]);
  openCadParcelFromMapRef.current = handleOpenCadParcelFromMap;

  const handleReturnToRegistrosFromCad = useCallback(() => {
    leaveCadView();
    setIsListsOpen(false);
    setOpenCadDetailRequest(null);
    setCadOpenedFromRegistros(false);
    setIsMyLotsOpen(true);
  }, [leaveCadView]);

  /** Bbox del lote madre en teselas cargadas, sin gastar cuota de detalle. */
  const queryCadRefLotBbox = useCallback((lotId) => {
    const map = mapInstanceRef.current;
    if (!map) return null;
    const layerIds = getInteractiveParcelLayerIds().filter((id) => map.getLayer(id));
    return queryCadRefLotBboxOnMap(map, lotId, {
      layerIds,
      sources: getActiveParcelSources(map),
    });
  }, [getActiveParcelSources, getInteractiveParcelLayerIds, mapInstanceRef]);

  const handleToggleMyLotsPanel = () => {
    if (!isLoggedInRef.current) return;
    setLeadReturnOrigin(null);
    if (cadOpenedFromRegistros) {
      handleReturnToRegistrosFromCad();
      return;
    }
    const opening = !isMyLotsOpen;
    if (opening) {
      clearLotPolygons();
      setLotLoadError(null);
      setIsLotLoading(false);
      setSelectedPolygon(null);
      setIsListsOpen(false);
      setIsHistoryOpen(false);
      setIsFilterResultsOpen(false);
      setOpenedFromRegistros(false);
    } else {
      setOpenedFromRegistros(false);
    }
    setIsMyLotsOpen(opening);
  };

  const handleToggleListsPanel = () => {
    if (!isLoggedInRef.current) return;
    if (cadOpenedFromRegistros) {
      leaveCadView();
      setOpenCadDetailRequest(null);
      setCadOpenedFromRegistros(false);
      setIsMyLotsOpen(false);
      setIsListsOpen(true);
      return;
    }
    if (leadReturnOrigin && isListsOpen) {
      // Igual que desde Registros: Listas en el rail va al índice (el key remonta el panel).
      leaveCadView();
      setOpenListDetailRequest(null);
      setOpenCadDetailRequest(null);
      setLeadReturnOrigin(null);
      return;
    }
    setLeadReturnOrigin(null);
    const opening = !isListsOpen;
    if (opening) {
      clearLotPolygons();
      setLotLoadError(null);
      setIsLotLoading(false);
      setSelectedPolygon(null);
      setIsMyLotsOpen(false);
      setIsHistoryOpen(false);
      setIsFilterResultsOpen(false);
      // Al abrir "Mis listas", no debe verse ningún resaltado previo.
      setMyLotsIds([]);
    }
    setIsListsOpen(opening);
  };

  const handleToggleHistoryPanel = () => {
    if (!isLoggedInRef.current || isViewingSharedSpace) return;
    setLeadReturnOrigin(null);
    const opening = !isHistoryOpen;
    if (opening) {
      clearLotPolygons();
      setLotLoadError(null);
      setIsLotLoading(false);
      setSelectedPolygon(null);
      setIsMyLotsOpen(false);
      setIsListsOpen(false);
      setIsFilterResultsOpen(false);
    }
    setIsHistoryOpen(opening);
  };

  const handleToggleLeadsPanel = () => {
    if (!isLoggedInRef.current) return;
    if (cadOpenedFromRegistros) {
      // Igual que Listas: salir del CAD abierto desde Registros.
      leaveCadView();
      setOpenCadDetailRequest(null);
      setCadOpenedFromRegistros(false);
    }
    const opening = !isLeadsOpen;
    setLeadUnitReturn(null);
    setLeadOpenRequest(null);
    setLeadReturnOrigin(null);
    if (opening) {
      clearLotPolygons();
      setLotLoadError(null);
      setIsLotLoading(false);
      setSelectedPolygon(null);
      setIsMyLotsOpen(false);
      setIsListsOpen(false);
      setIsHistoryOpen(false);
      setIsFilterResultsOpen(false);
    }
    setIsLeadsOpen(opening);
  };

  /** Abre / cierra Meta Ads con el mismo protocolo que Leads (cierra los demás paneles). */
  const openMetaAdsPanel = useCallback((view, options = null) => {
    setMetaAdsInitialView(view === 'campaigns' ? 'campaigns' : 'checklist');
    const focusId = view === 'campaigns' && options?.campaignId ? String(options.campaignId) : null;
    setMetaAdsFocusCampaign((prev) => ({ campaignId: focusId, seq: (prev?.seq || 0) + 1 }));
    if (cadOpenedFromRegistros) {
      leaveCadView();
      setOpenCadDetailRequest(null);
      setCadOpenedFromRegistros(false);
    }
    clearLotPolygons();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setIsMyLotsOpen(false);
    setIsListsOpen(false);
    setIsHistoryOpen(false);
    setIsLeadsOpen(false);
    setIsFilterResultsOpen(false);
    setIsMetaAdsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadOpenedFromRegistros, leaveCadView]);

  // E4: la sección «Campañas» de las fichas lee la conexión y abre el panel desde acá.
  // `null` (sin flag, 404 o 403) → la sección no existe. Ver docs/maps/meta-ads.md.
  const metaAdsViewerUserId = user?.sub || null;
  // Chip de unidad vinculada en Campañas → misma apertura que un interés del lead
  // (`handleOpenLeadItemTarget`, más abajo; ref para que el valor del contexto sea estable).
  const metaOpenRecordTargetRef = useRef(null);
  const openMetaRecordTarget = useCallback((target) => metaOpenRecordTargetRef.current?.(target), []);
  // F2a: después de una acción en Meta (pausar, reactivar, renombrar) las fichas abiertas recargan
  // su sección «Campañas» (`campaignsVersion` cambia).
  const [metaCampaignsVersion, setMetaCampaignsVersion] = useState(0);
  const invalidateMetaCampaigns = useCallback(() => setMetaCampaignsVersion((n) => n + 1), []);
  const metaAdsContextValue = useMemo(() => (showMetaAdsNav
    ? {
      metaConnection, openPanel: openMetaAdsPanel, viewerUserId: metaAdsViewerUserId, openRecordTarget: openMetaRecordTarget,
      campaignsVersion: metaCampaignsVersion, invalidateCampaigns: invalidateMetaCampaigns,
    }
    : null), [showMetaAdsNav, metaConnection, openMetaAdsPanel, metaAdsViewerUserId, openMetaRecordTarget, metaCampaignsVersion, invalidateMetaCampaigns]);

  const handleToggleMetaAdsPanel = () => {
    if (!isLoggedInRef.current) return;
    if (isMetaAdsOpen) {
      setIsMetaAdsOpen(false);
      return;
    }
    openMetaAdsPanel();
  };

  /**
   * Bandeja de consultas → ficha del lead (L2). Cierra Listas como su onClose
   * (incluido el CAD) y abre Leads directo en el detalle.
   */
  const handleOpenLeadFromInquiry = useCallback((leadId) => {
    const id = String(leadId || '').trim();
    if (!id || !isLoggedInRef.current) return;
    if (cadOpenedFromRegistros) {
      setCadOpenedFromRegistros(false);
    }
    setLeadReturnOrigin(null);
    setLeadUnitReturn(null);
    setSharedListLotForMap(null);
    setOpenListDetailRequest(null);
    setOpenCadDetailRequest(null);
    leaveCadView();
    clearLotPolygons();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setIsMyLotsOpen(false);
    setIsListsOpen(false);
    setIsHistoryOpen(false);
    setIsFilterResultsOpen(false);
    setLeadOpenRequest({ leadId: id, ts: Date.now() });
    setIsLeadsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadOpenedFromRegistros, leaveCadView]);

  /**
   * Volver al lead (flecha / cierre del lote o de Listas abiertos desde un
   * interés). Cierra lo abierto y reabre Leads en la ficha, igual que la
   * bandeja (`handleOpenLeadFromInquiry`). Si el lead ya no existe, el GET da
   * 404 → toast y la ficha vuelve al índice (LeadDetail).
   */
  const handleReturnToLead = useCallback(() => {
    const origin = leadReturnOrigin;
    handleCloseDockPanel();
    if (!origin || !isLeadReturnOriginInSpace(origin, isViewingSharedSpace ? activeOwnerUserId : null)) {
      setSharedListLotForMap(null);
      setOpenListDetailRequest(null);
      setOpenCadDetailRequest(null);
      leaveCadView();
      setIsListsOpen(false);
      return;
    }
    handleOpenLeadFromInquiry(origin.leadId);
  }, [leadReturnOrigin, handleCloseDockPanel, isViewingSharedSpace, activeOwnerUserId, leaveCadView, handleOpenLeadFromInquiry]);

  const handleDockPanelClose = useCallback(() => {
    if (openedFromRegistros) {
      handleBackToRegistros();
      return;
    }
    if (leadReturnOrigin) {
      handleReturnToLead();
      return;
    }
    handleCloseDockPanel();
  }, [openedFromRegistros, handleBackToRegistros, leadReturnOrigin, handleReturnToLead, handleCloseDockPanel]);

  /** «Ver en el mapa» (tarjeta / informe de mercado): entra al modo Comparables o lo actualiza. */
  const handleShowMarketComparables = useCallback((payload) => {
    if (!payload?.center || !payload.recordId) return;
    if (!marketViewRef.current) {
      marketCameraRef.current = snapshotMapCamera(mapInstanceRef.current);
    }
    marketViewSeqRef.current += 1;
    setMarketComparablesView({ ...payload, seq: marketViewSeqRef.current });
  }, [mapInstanceRef]);

  /** Filtros / operación nuevos desde el informe abierto en el modo (vuelve a encuadrar). */
  const handleChangeMarketComparablesView = useCallback((patch) => {
    marketViewSeqRef.current += 1;
    const seq = marketViewSeqRef.current;
    setMarketComparablesView((prev) => (prev ? { ...prev, ...patch, seq } : prev));
  }, []);

  /** Salir: capas afuera (el modo se desmonta), pines de vuelta y encuadre de la ficha. */
  const handleExitMarketComparables = useCallback(() => {
    const camera = marketCameraRef.current;
    marketCameraRef.current = null;
    setMarketComparablesView(null);
    if (camera) restoreMapCamera(mapInstanceRef.current, camera);
  }, [mapInstanceRef]);

  // Se limpia solo si la ficha de origen se cierra o cambia (clic en otra parcela, Registros,
  // logout): sin restaurar el encuadre, manda lo nuevo.
  useEffect(() => {
    if (shouldExitMarketComparables(marketViewRef.current, {
      selectedPolygonId: selectedPolygon ? (selectedPolygon.id ?? '') : null,
      isListsOpen,
      isLoggedIn,
    })) {
      marketCameraRef.current = null;
      setMarketComparablesView(null);
    }
  }, [selectedPolygon, isListsOpen, isLoggedIn]);

  /** Lead abierto desde "Consultas y leads" de la UF o del lote (DockPanel). */
  const handleOpenLeadFromUnit = useCallback((leadId, unit) => {
    const ctx = unit && typeof unit === 'object' ? unit : {};
    const origin = buildLeadRecordReturn(
      { leadId, ...ctx },
      isViewingSharedSpace ? activeOwnerUserId : null,
    );
    handleOpenLeadFromInquiry(leadId);
    // `handleOpenLeadFromInquiry` limpia `leadUnitReturn`; se setea después.
    setLeadUnitReturn(origin);
  }, [handleOpenLeadFromInquiry, isViewingSharedSpace, activeOwnerUserId]);

  /** Listas: `(leadId)` como siempre; con `returnCtx` (parcela CAD) deja el "Volver a parcela …". */
  const handleOpenLeadFromLists = useCallback((leadId, returnCtx) => {
    if (returnCtx) handleOpenLeadFromUnit(leadId, returnCtx);
    else handleOpenLeadFromInquiry(leadId);
  }, [handleOpenLeadFromUnit, handleOpenLeadFromInquiry]);

  /** "Volver a UF … / Lote … / parcela …" desde el detalle del lead: reabre el registro sin editar. */
  const handleReturnToUnit = useCallback(() => {
    const origin = leadUnitReturn;
    setLeadUnitReturn(null);
    setIsLeadsOpen(false);
    setLeadOpenRequest(null);
    if (!origin || !isLeadReturnOriginInSpace(origin, isViewingSharedSpace ? activeOwnerUserId : null)) return;
    if (origin.parcelId && origin.project?.slug) {
      // Parcela CAD: misma apertura que el mapa (Listas → detalle del proyecto).
      const { project } = origin;
      handleOpenCadParcelFromMap({
        id: origin.parcelId,
        project_slug: project.slug,
        project_name: project.name,
        project_id: project.id,
        display_mode: project.display_mode,
        can_manage: project.can_manage === true,
        can_publish: project.can_publish === true,
      });
      return;
    }
    if (origin.userLotId) {
      setPendingUserLotView(true);
      setPendingEditUserLotId(origin.userLotId);
    }
    fetchLotById(origin.lotId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadUnitReturn, isViewingSharedSpace, activeOwnerUserId, handleOpenCadParcelFromMap]);

  /**
   * Ficha del lead → interés tocado (ver `useLeadItemOpener`). Reusa las
   * mismas aperturas que Historial (lote), filtros (lista) y el mapa (CAD);
   * abrir cualquiera de esos cierra Leads (efecto de abajo). Con `fromLead`
   * queda el origen para “Volver a Lead: …” (como `openedFromRegistros`).
   */
  const handleOpenLeadItemTarget = (target) => {
    if (!target || !isLoggedInRef.current) return;
    const origin = buildLeadReturnOrigin(target.fromLead, isViewingSharedSpace ? activeOwnerUserId : null);
    if (target.type === 'lot' && target.lotId) {
      // Chip de unidad: abre la UF directo (sin editar), como el volver a la UF.
      if (target.userLotId) {
        setPendingUserLotView(true);
        setPendingEditUserLotId(target.userLotId);
      }
      fetchLotById(target.lotId, { leadReturnOrigin: origin });
      return;
    }
    if (target.type === 'list' && target.listId) {
      clearLotPolygons();
      setLotLoadError(null);
      setIsLotLoading(false);
      setSelectedPolygon(null);
      setSharedListLotForMap(null);
      setOpenCadDetailRequest(null);
      setIsMyLotsOpen(false);
      setIsHistoryOpen(false);
      setIsFilterResultsOpen(false);
      setMyLotsIds([]);
      setOpenListDetailRequest({
        listId: target.listId,
        listName: target.listName,
        ts: Date.now(),
      });
      setLeadReturnOrigin(origin);
      setIsListsOpen(true);
      return;
    }
    if (target.type === 'cad' && target.project?.slug) {
      const project = target.project;
      if (target.parcelId) {
        handleOpenCadParcelFromMap({
          id: target.parcelId,
          title: target.parcelLabel || '',
          project_slug: project.slug,
          project_name: project.name,
          project_id: project.id,
          display_mode: project.display_mode,
          can_manage: project.can_manage,
          can_publish: project.can_publish,
        });
        setLeadReturnOrigin(origin);
        return;
      }
      handleOpenCadListFromMap(project);
      setLeadReturnOrigin(origin);
    }
  };
  metaOpenRecordTargetRef.current = handleOpenLeadItemTarget;

  // Leads es un panel más: si se abre otro (o una ficha de lote), se cierra.
  useEffect(() => {
    if (!isLeadsOpen) return;
    if (
      isMyLotsOpen
      || isListsOpen
      || isHistoryOpen
      || isFilterResultsOpen
      || cadOpenedFromRegistros
      || selectedPolygon
      || isLotLoading
    ) {
      setIsLeadsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyLotsOpen, isListsOpen, isHistoryOpen, isFilterResultsOpen, cadOpenedFromRegistros, selectedPolygon, isLotLoading]);

  // Meta Ads también es un panel más: si se abre otro (o una ficha de lote), se cierra.
  useEffect(() => {
    if (!isMetaAdsOpen) return;
    if (
      isMyLotsOpen
      || isListsOpen
      || isHistoryOpen
      || isLeadsOpen
      || isFilterResultsOpen
      || cadOpenedFromRegistros
      || selectedPolygon
      || isLotLoading
    ) {
      setIsMetaAdsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMyLotsOpen, isListsOpen, isHistoryOpen, isLeadsOpen, isFilterResultsOpen, cadOpenedFromRegistros, selectedPolygon, isLotLoading]);

  // Sin flag, 404 de la API o sin acceso al espacio: la sección no existe.
  useEffect(() => {
    if (isMetaAdsOpen && metaConnection.hidden) setIsMetaAdsOpen(false);
  }, [isMetaAdsOpen, metaConnection.hidden]);

  // `/#/meta-ads` (vuelta del callback de Meta): abre el panel y deja la URL en /app.
  useEffect(() => {
    if (location.pathname !== META_ADS_PANEL_PATH || authLoading) return;
    if (metaAdsEnabled) openMetaAdsPanel();
    navigate('/app', { replace: true });
  }, [location.pathname, authLoading, metaAdsEnabled, openMetaAdsPanel, navigate]);

  const handleOpenFilterResultsList = useCallback(() => {
    if (activeFilterCount === 0) return;
    if (isFilterResultsOpen) {
      setIsFilterResultsOpen(false);
      return;
    }
    clearLotPolygons();
    setLotLoadError(null);
    setIsLotLoading(false);
    setSelectedPolygon(null);
    setOpenedFromRegistros(false);
    setLeadReturnOrigin(null);
    setIsHistoryOpen(false);
    setIsFilterResultsOpen(false);

    if (filterScope?.type === 'myLots') {
      setIsListsOpen(false);
      setIsMyLotsOpen(true);
      return;
    }

    if (filterScope?.type === 'list' && filterScope.listId) {
      setIsMyLotsOpen(false);
      setMyLotsIds([]);
      setOpenListDetailRequest({
        listId: filterScope.listId,
        listName: filterScope.listName,
        ts: Date.now(),
      });
      setIsListsOpen(true);
      return;
    }

    setIsMyLotsOpen(false);
    setIsListsOpen(false);
    setIsFilterResultsOpen(true);
  }, [activeFilterCount, isFilterResultsOpen, clearLotPolygons, filterScope]);

  const handleToggleFavorite = async (lotId) => {
    const isFav = favoriteIds.includes(lotId);
    if (isFav) {
      await removeFavorite(lotId);
      setFavorites((prev) => prev.filter((f) => f.id !== lotId));
    } else {
      await addFavorite(lotId);
      const res = await getFavorites();
      if (res.data) setFavorites(res.data);
    }
  };

  const uiLeftInset = isLoggedIn ? SIDEBAR_RAIL_WIDTH : 0;
  const desktopPanelOccupiedWidth = getDesktopPanelOccupiedWidth(uiLeftInset);
  const breadcrumbAvoidsPanel = hasOpenPanel && isDesktop;
  // Chip centrado en viewport; con panel abierto solo compactamos / limitamos ancho.
  const breadcrumbMaxWidth = breadcrumbAvoidsPanel
    ? `min(calc(100vw - ${desktopPanelOccupiedWidth}px - 96px), 640px)`
    : undefined;
  const currentZoom = Number.parseFloat(mapInfo.zoom);
  const officialUnitZoomLocked = Number.isFinite(currentZoom)
    && currentZoom < OFFICIAL_UNIT_FILTER_MIN_ZOOM;
  const showZoomParcelsHint = (
    !isAdministrativeMapLoading
    && !isListViewportLoading
    && !cadViewSettling
    && Number.isFinite(currentZoom)
    && currentZoom >= 10
    && currentZoom < 14
  );
  const showParcelasZoomHint = (
    showStatusOverlays
    && !showZonificacionUrbanaCoverageHint
    && (
      (showZoomParcelsHint && !parcelsZoomHintDismissed)
      || showParcelasToggleZoomHint
    )
  );
  const parcelsZoomHintText = showParcelasToggleZoomHint
    ? PARCELS_ZOOM_TOGGLE_HINT_TEXT
    : PARCELS_ZOOM_PASSIVE_HINT_TEXT;
  const handleDismissParcelsZoomHint = () => {
    if (showParcelasToggleZoomHint) {
      setShowParcelasToggleZoomHint(false);
      return;
    }
    setParcelsZoomHintDismissed(true);
  };
  const showAdministrativeBreadcrumb = Boolean(
    !cadPlanOpen && (selectedProvinceName || selectedDepartmentName || selectedPedaniaName || selectedMunicipalityName)
  );
  // En mobile, con login/registro abierto ocultamos búsqueda+filtros para que el modal no se corte.
  // Desktop mantiene búsqueda/filtros en el header como antes.
  const isAuthFormOpen = AUTH_FORM_PATHS.includes(location.pathname);
  const showMapSearchChrome = !(isMobile && isAuthFormOpen);
  const mobileSearchChromeOffset = isMobile && showHeader && showMapSearchChrome
    ? MOBILE_SEARCH_BAR_BLOCK_HEIGHT
    : 0;

  return (
    <MetaAdsProvider value={metaAdsContextValue}>
      {showHeader && (
        <MapHeader
          isLoggedIn={isLoggedIn}
          user={user}
          onLoginClick={() => {
            handleCloseDockPanel();
            navigate('/login');
          }}
          onLogoutClick={handleLogout}
          onToggleMyLots={handleToggleMyLotsPanel}
          isMyLotsOpen={isMyLotsOpen || cadOpenedFromRegistros}
          onToggleLists={handleToggleListsPanel}
          isListsOpen={isListsOpen && !cadOpenedFromRegistros}
          onToggleHistory={isViewingSharedSpace ? undefined : handleToggleHistoryPanel}
          isHistoryOpen={isHistoryOpen}
          onToggleLeads={handleToggleLeadsPanel}
          isLeadsOpen={isLeadsOpen}
          onToggleMetaAds={showMetaAdsNav ? handleToggleMetaAdsPanel : undefined}
          isMetaAdsOpen={isMetaAdsOpen}
          onOpenBranding={openBrands}
          onOpenFeedback={() => setFeedbackOpen(true)}
          onRestartTour={restartTour}
          navDrawerOpen={isNavDrawerOpen}
          onNavDrawerOpenChange={setIsNavDrawerOpen}
          showSearchControl={showMapSearchChrome}
          showFilterControl={showMapSearchChrome}
          showListingFilters={isFilterScopeActive}
          showOfficialUnitFilters={
            !selectedProvinceName || provinceHasOfficialUnitFilters(selectedProvinceName)
          }
          showOfficialUnitCoverageNote={Boolean(
            selectedProvinceName && !provinceHasOfficialUnitFilters(selectedProvinceName),
          )}
          officialUnitFilter={officialUnitFilter}
          officialUnitZoomLocked={officialUnitZoomLocked}
          lotViewQuotaExceeded={lotViewQuotaExceeded}
          lotViewQuotaResetsAt={lotViewQuota?.resetsAt || null}
          activeFilters={activeFilters}
          activeFilterCount={headerFilterCount}
          showOnlyFiltered={showOnlyFiltered}
          onSetFilterValue={handleSetFilterValue}
          onRemoveFilterChip={handleRemoveFilterChip}
          onToggleShowOnlyFiltered={handleToggleShowOnlyFiltered}
          onClearAllFilters={handleClearAllFilters}
          onOpenFilterResultsList={
            isLoggedIn && activeFilterCount > 0 && !lotViewQuotaExceeded
              ? handleOpenFilterResultsList
              : undefined
          }
          isFilterResultsOpen={isFilterResultsOpen}
          filterScope={filterScope}
          filterScopeLists={filterScopeSelectLists}
          filterScopeListsLoading={filterScopeListsLoading}
          filterScopeLoading={filterScopeLoading}
          filterScopeError={filterScopeError}
          filterScopeCount={effectiveScopeIds.length}
          filterScopeMatchCount={filterScopeMatchedLots.length}
          mapFilterOptions={panelFilterOptions}
          onRequestMapFilterOptions={handleRequestMapFilterOptions}
          onSetFilterScope={handleSetFilterScope}
          onFilterScopeDropdownOpen={ensureFilterScopeListsLoaded}
          filterScopeLocked={isFilterScopeLocked}
          onFlyTo={handleFlyTo}
          parseSearchEnabled={isNlSearchEnabled()}
          resolveViewProvince={resolveViewProvinceForParse}
          onApplyParsedLotFilters={handleApplyParsedLotFilters}
          onLotViewQuotaInfo={applyLotViewQuotaFromLot}
          {...(cadHeaderFilterProps || {})}
        />
      )}

      {showControls && !cadPlanOpen && (
        <MapControls
          mapView={mapView}
          onSelectMapView={handleSelectMapView}
          showParcelas={showParcelas}
          onToggleParcelas={handleToggleParcelas}
          parcelasStateFilter={parcelasStateFilter}
          onParcelasFilterChange={handleParcelasFilterChange}
          isLoggedIn={isLoggedIn}
          onCenterOnUserLocation={handleCenterOnUserLocation}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          mapInfo={mapInfo}
          liveMap={viewMap}
          disabledMessage=""
          canSendFeedback={isLoggedIn}
          hideFeedbackButton={hideMobileFeedbackButton}
          feedbackOpen={feedbackOpen}
          onFeedbackOpenChange={setFeedbackOpen}
          showPobrezaCronicaArgentina={showPobrezaCronicaArgentina}
          pobrezaCronicaArgentinaOpacity={pobrezaCronicaArgentinaOpacity}
          onTogglePobrezaCronicaArgentina={() => {
            if (!isLoggedIn) return;
            setShowPobrezaCronicaArgentina((prev) => !prev);
          }}
          onPobrezaCronicaArgentinaOpacityChange={(nextOpacity) => {
            setPobrezaCronicaArgentinaOpacity(nextOpacity);
          }}
          showZonasInundables={showZonasInundables}
          onToggleZonasInundables={() => {
            if (!isLoggedIn) return;
            setShowZonasInundables((prev) => !prev);
          }}
          showZonificacionUrbana={showZonificacionUrbana}
          onToggleZonificacionUrbana={handleToggleZonificacionUrbana}
          mapBearing={mapBearing}
          mapPitch={mapPitch}
          onResetNorth={handleResetNorth}
          onResetPitch={handleResetPitch}
          deferFeedbackHint={deferFeedbackHint}
          feedbackHintTrigger={feedbackHintTrigger}
          topOffset={mobileSearchChromeOffset}
          {...referencePlacesControlProps}
        />
      )}

      {isLoggedIn && isMapRoute && (
        <ProductTour
          run={tourRun && Boolean(activeTourSteps?.length)}
          steps={activeTourSteps || tourSteps}
          onCallback={handleTourCallback}
        />
      )}

      <AdministrativeBreadcrumb
        show={showStatusOverlays && showAdministrativeBreadcrumb}
        showHeader={showHeader}
        topOffset={mobileSearchChromeOffset}
        maxWidth={breadcrumbMaxWidth}
        compactTrail={breadcrumbAvoidsPanel || !isDesktop}
        selectedProvinceName={selectedProvinceName}
        selectedDepartmentName={selectedDepartmentName}
        selectedPedaniaName={selectedPedaniaName}
        selectedMunicipalityName={selectedMunicipalityName}
        onClear={handleClearAdministrativeSelection}
        onProvinceClick={
          selectedProvinceName
            ? () => handleFocusAdministrativeLevel('province')
            : undefined
        }
        onDepartmentClick={
          selectedDepartmentName
            ? () => handleFocusAdministrativeLevel('department')
            : undefined
        }
        onPedaniaClick={
          selectedPedaniaName
            ? () => handleFocusAdministrativeLevel('pedania')
            : undefined
        }
        onMunicipalityClick={
          selectedMunicipalityName
            ? () => handleFocusAdministrativeLevel('municipality')
            : undefined
        }
      />

      {locationMessage && showStatusOverlays && !isLoggedIn && (
        <div
          style={{
            position: 'absolute',
            bottom: '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            backgroundColor: 'rgba(255,255,255,0.96)',
            color: '#111827',
            padding: '10px 14px',
            borderRadius: '10px',
            fontSize: '13px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.14)',
          }}
        >
          {locationMessage}
        </div>
      )}

      {showParcelasZoomHint && (
        <MapStatusBanner
          icon={<ZoomInIcon sx={{ fontSize: 18 }} />}
          onClose={handleDismissParcelsZoomHint}
        >
          {parcelsZoomHintText}
        </MapStatusBanner>
      )}

      {showZonificacionUrbanaCoverageHint && (
        <MapStatusBanner
          icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
          onClose={handleDismissZonificacionUrbanaCoverageHint}
        >
          {zonificacionUrbanaCoverageMessage}
        </MapStatusBanner>
      )}

      {showStatusOverlays && isAdministrativeMapLoading && !mapUnavailable && (
        <MapStatusBanner icon={<span style={mapStatusBannerSpinnerStyle} />}>
          {MAP_LOADING_MESSAGE}
        </MapStatusBanner>
      )}

      {showStatusOverlays && (isListViewportLoading || cadViewSettling) && (
        <MapStatusBanner
          icon={<span style={mapStatusBannerSpinnerStyle} />}
          style={{ zIndex: 1001 }}
        >
          {LIST_VIEWPORT_LOADING_MESSAGE}
        </MapStatusBanner>
      )}

      {/* Modo Comparables: la ficha queda montada (estado y scroll) pero oculta. */}
      <div style={{ display: marketModeOn ? 'none' : 'contents' }} data-testid="map-dock-slot">
      <DockPanel
        uiLeftInset={uiLeftInset}
        polygonInfo={selectedPolygon}
        isLoading={isLotLoading}
        loadError={lotLoadError}
        openInEditMode={openDockInEditMode}
        onEditModeConsumed={() => setOpenDockInEditMode(false)}
        initialUserLotId={pendingEditUserLotId}
        initialUserLotEdit={!pendingUserLotView}
        onInitialUserLotIdConsumed={() => {
          setPendingEditUserLotId(null);
          setPendingUserLotView(false);
        }}
        initialOpenLotContent={pendingOpenLotContent}
        onInitialOpenLotContentConsumed={handleInitialOpenLotContentConsumed}
        listContextLabel={openedFromRegistros ? 'Registros' : getLeadReturnLabel(leadReturnOrigin)}
        onClose={handleDockPanelClose}
        onOpenLead={isLoggedIn ? handleOpenLeadFromUnit : undefined}
        onUpdate={isLoggedIn ? handleUpdatePolygon : undefined}
        isFavorite={isLoggedIn && selectedPolygon?.id ? favoriteIds.includes(selectedPolygon.id) : false}
        onToggleFavorite={isLoggedIn ? handleToggleFavorite : undefined}
        lotBuildingDisplayMode={lotBuildingDisplayMode}
        onLotBuildingDisplayModeChange={setLotBuildingDisplayMode}
        lotBuildingStatus={lotBuildingStatus}
        onShowMarketComparables={isLoggedIn ? handleShowMarketComparables : undefined}
      />
      </div>

      {marketComparablesView ? (
        <MarketComparablesMode
          mapInstanceRef={mapInstanceRef}
          view={marketComparablesView}
          uiLeftInset={uiLeftInset}
          fitPadding={getMapPageLotFitPadding(isMobile)}
          isMobile={isMobile}
          onExit={handleExitMarketComparables}
          onChangeView={handleChangeMarketComparablesView}
        />
      ) : null}

      {isLoggedIn && isMyLotsOpen && (
        <RegistrosPanel
          uiLeftInset={uiLeftInset}
          hidden={Boolean(selectedPolygon) || openedFromRegistros || cadOpenedFromRegistros}
          onClose={() => {
            setOpenedFromRegistros(false);
            setCadOpenedFromRegistros(false);
            setIsMyLotsOpen(false);
          }}
          onOpenLot={(lotId, options = {}) => {
            setOpenedFromRegistros(true);
            setPendingOpenLotContent(Boolean(options.openLotContent));
            fetchLotById(lotId, { keepRegistrosOpen: true });
          }}
          onLocateLot={handleLocateLot}
          onLocateCadParcel={handleLocateCadParcel}
          onOpenCadParcel={handleOpenCadParcel}
          onAddLot={(lotId, userLotId) => {
            setOpenedFromRegistros(true);
            setPendingEditUserLotId(userLotId || null);
            setPendingUserLotView(false);
            setOpenDockInEditMode(true);
            fetchLotById(lotId, { keepRegistrosOpen: true });
          }}
          onHighlightLotIdsChange={setMyLotsIds}
          onHighlightLotsChange={setMyLotsHighlightLots}
          onFitInView={handleFitMyLotsInView}
          parcelFilters={activeFilters}
          onWorkingSetChange={setRegistrosWorkingLots}
        />
      )}

      {isLoggedIn && isListsOpen && !selectedPolygon && !isLotLoading && !lotLoadError && (
        <div style={{ display: marketModeOn ? 'none' : 'contents' }}>
        <UserLotListsPanel
          key={cadOpenedFromRegistros ? 'cad-from-registros' : leadReturnOrigin ? 'from-lead' : 'lists'}
          uiLeftInset={uiLeftInset}
          onOpenLead={handleOpenLeadFromLists}
          onShowMarketComparables={handleShowMarketComparables}
          openListDetailRequest={openListDetailRequest}
          openCadDetailRequest={openCadDetailRequest}
          onReturnToOrigin={
            cadOpenedFromRegistros
              ? handleReturnToRegistrosFromCad
              : leadReturnOrigin ? handleReturnToLead : undefined
          }
          returnOriginLabel={cadOpenedFromRegistros ? 'Registros' : getLeadReturnLabel(leadReturnOrigin)}
          onClose={() => {
            if (cadOpenedFromRegistros) {
              handleReturnToRegistrosFromCad();
              return;
            }
            if (leadReturnOrigin) {
              handleReturnToLead();
              return;
            }
            setSharedListLotForMap(null);
            setOpenListDetailRequest(null);
            setOpenCadDetailRequest(null);
            leaveCadView();
            setIsListsOpen(false);
          }}
          onCadViewChange={handleCadViewChange}
          onCadOverlayChange={handleCadOverlayChange}
          onCadListOpenChange={setCadOpenList}
          onCadOwnerOverlayInvalidate={handleCadOwnerOverlayInvalidate}
          onCadProjectPinSync={handleCadProjectPinSync}
          onCadFitInView={handleCadFitInView}
          onOpenLot={(lotId) => {
            fetchLotById(lotId);
            setIsListsOpen(false);
          }}
          onSharedLotDockChange={handleSharedLotDockChange}
          onLocateLot={handleLocateLot}
          onQueryCadRefLotBbox={queryCadRefLotBbox}
          onFitInView={handleFitListsInView}
          onHighlightLotIdsChange={setListHighlightIds}
          onHighlightLotsChange={setListHighlightLots}
          onPendingAddLotIdsChange={setListPendingAddIds}
          onPendingAddLotsChange={setListPendingAddLots}
          parcelFilters={activeFilters}
          cadParcelFilters={cadListFilters.activeFilters}
          onCadFilterValueChange={cadListFilters.setFilterValue}
          onFilterScopeContextChange={setListFilterContext}
          mapLotClickRequest={listLotMapClickRequest}
          mapHoveredLotId={mapHoveredListLotId}
          onListRecordHover={handleListRecordHover}
          onListDetailHoverActive={setListDetailHoverActive}
          mapAddPickRequest={listMapAddPickRequest}
          mapGeorefPickRequest={cadGeorefPickRequest}
          mapPinPlaceRequest={cadPinPlaceRequest}
          onListAddPickModeChange={handleListAddPickModeChange}
          lotBuildingDisplayMode={lotBuildingDisplayMode}
          onLotBuildingDisplayModeChange={setLotBuildingDisplayMode}
          lotBuildingStatus={lotBuildingStatus}
        />
        </div>
      )}

      {isLoggedIn && isHistoryOpen && !isViewingSharedSpace && !selectedPolygon && !isLotLoading && !lotLoadError && (
        <HistoryPanel
          uiLeftInset={uiLeftInset}
          onClose={() => setIsHistoryOpen(false)}
          onOpenLot={(lotId) => {
            fetchLotById(lotId);
            setIsHistoryOpen(false);
          }}
          onLocateLot={handleLocateLot}
          onHighlightLotIdsChange={setHistoryLocatedLotIds}
        />
      )}

      {isLoggedIn && isLeadsOpen && !selectedPolygon && !isLotLoading && !lotLoadError && (
        <LeadsPanel
          key={isViewingSharedSpace && activeOwnerUserId ? `leads-${activeOwnerUserId}` : 'leads-own-space'}
          ownerUserId={isViewingSharedSpace ? activeOwnerUserId : null}
          uiLeftInset={uiLeftInset}
          openLeadRequest={leadOpenRequest}
          onOpenItemTarget={handleOpenLeadItemTarget}
          unitReturn={leadUnitReturn}
          onUnitReturn={handleReturnToUnit}
          onClose={() => {
            setLeadUnitReturn(null);
            setIsLeadsOpen(false);
            setLeadOpenRequest(null);
          }}
        />
      )}

      {isLoggedIn && isMetaAdsOpen && !metaConnection.hidden && !selectedPolygon && !isLotLoading && !lotLoadError && (
        <MetaAdsPanel
          key={isViewingSharedSpace && activeOwnerUserId ? `meta-ads-${activeOwnerUserId}` : 'meta-ads-own-space'}
          metaConnection={metaConnection}
          viewerUserId={metaAdsViewerUserId}
          initialView={metaAdsInitialView}
          focusCampaign={metaAdsFocusCampaign}
          uiLeftInset={uiLeftInset}
          onClose={() => setIsMetaAdsOpen(false)}
        />
      )}

      {isLoggedIn && isFilterResultsOpen && (
        <FilterResultsPanel
          uiLeftInset={uiLeftInset}
          hidden={filterResultsPanelHidden}
          lots={filterResultsLots}
          truncated={filterViewportTruncated}
          isSampleView={filterViewportIsSample}
          isRefreshing={filterViewportRefreshing}
          hasSettled={filterViewportSettled}
          onClose={() => setIsFilterResultsOpen(false)}
          onOpenLot={(lotId) => {
            fetchLotById(lotId, { keepFilterResultsOpen: true });
          }}
          onLocateLot={handleLocateLot}
          onHighlightLotIdsChange={setFilterResultsLocatedLotIds}
        />
      )}

      <MapErrorBoundary variant="page" surface="app">
        <div
          ref={mapRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            visibility: cadPlanOpen ? 'hidden' : 'visible',
            pointerEvents: cadPlanOpen ? 'none' : 'auto',
          }}
        />
      </MapErrorBoundary>
      {mapUnavailable && !cadPlanOpen ? (
        <MapUnavailable variant="page" reason={mapUnavailableReason} />
      ) : null}
      {cadViewSettling ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            bgcolor: withAlpha(colors.backgroundDark, 0.62),
            pointerEvents: 'auto',
          }}
          aria-hidden
        />
      ) : null}
      {cadPlanOpen ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            bgcolor: colors.backgroundDark,
          }}
        >
          <MapErrorBoundary surface="app-plan">
            <ShowroomPlanStage
              project={cadOverlay.project}
              featureCollection={cadOverlay.featureCollection}
              selectedId={cadOverlay.selectedId}
              onSelect={cadOverlay.onSelect}
              onPlanClick={cadOverlay.onPlanClick}
              onPinSelect={cadOverlay.onPinSelect}
              highlightedPinId={cadOverlay.highlightedPinId}
              imageTransform={cadOverlay.imageTransform}
              imagePaintUrl={cadOverlay.imagePaintUrl}
              onImageTransformChange={cadOverlay.onImageTransformChange}
              adjustImage={cadOverlay.adjustImage}
              isLoggedIn={isLoggedIn}
              fitPadding={cadFitPadding}
              topOffset={mobileSearchChromeOffset}
              fitRef={cadPlanFitRef}
              pickContext
            />
          </MapErrorBoundary>
        </Box>
      ) : null}
      <style>
        {`
          @keyframes map-loader-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </MetaAdsProvider>
  );
};

export default MapPage;
